import * as THREE from "three";
import type { QualityLevel } from "@host/types.js";
import type { ArenaAnchor, PhaseSpec, ReceiverTier } from "../simulation/types.js";
import { makeFloorTextures, makeGlassTextures, makeHazardTexture, makeSteelTexture } from "../render/textures.js";
import committedArena from "./arena.v1.json";
import {
  deriveArenaGeometry,
  ENCLOSURE_INTERIOR_HEIGHT,
  isChutePiece,
  parseArenaDefinition,
  type ArenaDefinition,
  type ArenaLabelDefinition,
  type ArenaPieceDefinition,
  type ChuteId,
  type Vec3,
} from "./ArenaDefinition.js";
import { CHUTE_SHELL, ENCLOSURE_SHELL, portalSurfaceOrientation, RAMP_SUPPORT } from "./ArenaPhysics.js";
import {
  applyHeroQuality,
  createHeroAsset,
  resolveFurnaceShimmer,
  resolveHeroGlowMaterial,
  seatFurnaceThroat,
  upgradeHeroAsset,
  type HeroAsset,
  type HeroAssetId,
} from "../render/HeroAssets.js";
import {
  ARENA_ACCENTS,
  ARENA_BOX_FALLBACK,
  ARENA_BOX_PRESETS,
  ARENA_TEXTURED_PRESETS,
  cssHex,
  ENCLOSURE,
  FEEDBACK_COLORS,
  GLASS,
  HERO_THEME,
  PALETTE,
  RECEIVER_TIER_COLORS,
  RETURN_ASSEMBLY,
  SIGNAGE,
} from "../render/theme.js";

export const ARENA_DEFINITION = parseArenaDefinition(committedArena);
export const ARENA_GEOMETRY = deriveArenaGeometry(ARENA_DEFINITION);

export interface AuthoredTarget {
  endpoint: "A" | "B";
  name: ArenaAnchor | ReceiverTier;
  pos: THREE.Vector3;
  normal: THREE.Vector3;
  visuals: THREE.Object3D[];
  label: THREE.Sprite;
  pieceId: string;
}

export class GrayBoxArena {
  readonly group = new THREE.Group();
  readonly aimTargets: THREE.Object3D[] = [];
  readonly authored = new Map<THREE.Object3D, AuthoredTarget>();
  readonly objects = new Map<string, THREE.Object3D>();
  readonly definition: ArenaDefinition;
  private railObject: THREE.Object3D | null = null;
  /** Sweep-clock multiplier for the rail panel; 1 = the authored slow sweep. See setRailRate. */
  private railRate = 1;
  private shutters: { left: THREE.Object3D | null; right: THREE.Object3D | null } = { left: null, right: null };
  private baffle: THREE.Object3D | null = null;
  private fireMat: THREE.MeshStandardMaterial | null = null;
  private fireLight: THREE.PointLight | null = null;
  /**
   * Simple-mode signage (LOCKED-DESIGN 18.11): when set, the identifier sign
   * chips drop to icon-first variants — an enlarged FORM glyph + color, text
   * stripped away. The QA/editor label seam (labelObjects / userData.identifier)
   * is unchanged, so STANDARD mode still finds every identifier; Simple mode
   * flags each repainted chip with userData.simpleSign for its own QA scenario.
   */
  private simpleSignage = false;
  private readonly labelProbe = new THREE.Vector3();
  // screenRect scratch set (18.8 craft: the declutter pass calls it every
  // rendered frame — pooled probes keep the hot loop allocation-free, same
  // convention as labelProbe / the Engine's tmpV)
  private readonly rectCenter = new THREE.Vector3();
  private readonly rectProjected = new THREE.Vector3();
  private readonly rectProbe = new THREE.Vector3();
  private readonly rectCorner = new THREE.Vector3();
  /** per-label reusable declutter entries + the frame's visible list (no per-frame churn) */
  private readonly declutterEntries = new Map<THREE.Sprite, { label: THREE.Sprite; rect: { x: number; y: number; width: number; height: number } }>();
  private readonly declutterVisible: Array<{ label: THREE.Sprite; rect: { x: number; y: number; width: number; height: number } }> = [];

  // ---- presentation-only state (LOCKED-DESIGN 18.4 feedback/telegraphs) ----
  // None of this touches ArenaPhysics; colliders derive from the definition only.
  /** reward flash per receiver lane, decayed in animate() */
  private readonly receiverPulseLevels: Record<ReceiverTier, number> = { safe: 0, technical: 0, hazard: 0 };
  /** mini-batch telegraph flags per chute */
  private readonly chuteTelegraphActive: Record<ChuteId, boolean> = { left: false, center: false, right: false };
  /** per-chute presentation handles (signal strip material + critical shutter plate) */
  private readonly chuteFx = new Map<ChuteId, {
    signal: THREE.MeshStandardMaterial;
    baseEmissive: number;
    shutter: THREE.Mesh;
    shutterMat: THREE.MeshStandardMaterial;
    openY: number;
    closedY: number;
  }>();
  /** critical overflow shutters all inbound chutes (Section 7) — 0 open, 1 closed */
  private chuteShuttersClosed = false;
  private chuteShutterProgress = 0;
  /** lane-shutter choreography: warning flash + sweep instead of a visibility pop */
  private readonly laneShutterFx = new Map<"left" | "right", {
    object: THREE.Object3D;
    strip: THREE.Mesh;
    stripMat: THREE.MeshBasicMaterial;
    baseZ: number;
    length: number;
    progress: number;
    targetClosed: boolean;
  }>();
  private shutterWarnLane: "left" | "right" | null = null;
  /** conveyor-reversal 5s warning: scroll chevrons toward the NEW direction */
  private readonly conveyorFx: Array<{ material: THREE.MeshStandardMaterial; texture: THREE.Texture; baseEmissive: number }> = [];
  private conveyorWarnDirection: 1 | -1 | 0 = 0;
  /** expiring-panel 5s warning keys (`A:wall`, `B:hazard`), pulsed in animate() */
  private readonly expiringPanels = new Set<string>();
  /**
   * Jam INVITE receiver (LOCKED-DESIGN 18.16): the lane the recovery prompt is
   * talking about right now. Rides the same authored-panel loop as the expiring
   * warning rather than adding a third highlight system — a steady raised glow
   * (never a strobe) plus a slow label breathe that stops under flash reduction.
   */
  private inviteReceiver: ReceiverTier | null = null;
  /** chamber-blast glass stress flicker (Section 5 'strong glass-shaking visual feedback') */
  private readonly glassFx: Array<{ material: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial; baseOpacity: number }> = [];
  private glassStressLevel = 0;
  /** reinforced-glass reflection tiering (18.1): pane envMap + additive sheen —
   * the sheen mesh drops on the low tier; there is NO per-frame planar pass. */
  private readonly glassPaneMats: THREE.MeshStandardMaterial[] = [];
  private readonly glassSheenMeshes: THREE.Object3D[] = [];
  /** hazard-baffle gate lamp (blocked = hazard red, open = safe green, null = dim) */
  private baffleLampMat: THREE.MeshStandardMaterial | null = null;
  private baffleGateBlocked: boolean | null = null;
  /**
   * Maw FEED GUARD (LOCKED-DESIGN 18.12): the re-dressed release-handle asset,
   * relocated to the intake lip and stenciled "FEED POINT". Glows conveyance-teal
   * while any receiver lane is jammed so the world agrees with the recovery prompt
   * (reuses the setJamActive hook). animate() pulses it (flash-aware).
   */
  private recoveryHandleMat: THREE.MeshStandardMaterial | null = null;
  private recoveryHandleBaseEmissive = 0;
  private jamActive = false;
  /** CHAMBER HEAT dwell fill (0..1): furnace emissive + rim warm scale with it (18.12). */
  private chamberHeat = 0;
  /** FURNACE OVERLOAD distress (0..1): glass glow ramps amber→red across the window (18.12). */
  private furnaceDistress = 0;
  /** Return-pad spin-up charge (0..1): pad ring fill during the launch spin-up (18.12). */
  private returnPadCharge = 0;
  private returnPadMat: THREE.MeshStandardMaterial | null = null;
  private returnPadBaseEmissive = 0;
  /**
   * Hero-asset mounts (LOCKED-DESIGN 18.3): procedural kit-bash machinery
   * behind the GLTF-ready seam — receivers, furnace maw, release handle. Each
   * mount remembers its asset so an authored .glb can hot-swap in and dispose
   * cleanly. The furnace throat/shimmer handles feed animate().
   */
  private readonly heroMounts = new Map<HeroAssetId, HeroAsset>();
  private furnaceShimmer: { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial; texture: THREE.Texture } | null = null;
  /**
   * Teardown latch for the async GLTF-upgrade probes (18.3 seam). The editor
   * rebuilds the arena on EVERY edit and Engine.destroy can run mid-probe;
   * without this flag a late-resolving authored model would mount into the
   * detached group and rebind live-state handles (recoveryPivot, fireMat,
   * furnaceShimmer, target.visuals) on a dead instance — leaking GLTF
   * geometry/materials per rebuild. Mirrors SceneRenderer's player-unit guard.
   */
  private disposed = false;
  private qualityLevel: QualityLevel = "high";
  /**
   * Reject-return conveyance presentation (Section 6 / 18.10 third dogfood):
   * the chamber collection belt scroll + the bright return hatch beside the
   * sorting island, lit while a return is incoming (setReturnIncoming).
   */
  private returnBeltFx: { material: THREE.MeshStandardMaterial; texture: THREE.Texture; baseEmissive: number } | null = null;
  private returnCollarMat: THREE.MeshStandardMaterial | null = null;
  private returnCollarBaseEmissive = 0;
  private returnPlate: { sprite: THREE.Sprite; canvas: HTMLCanvasElement; texture: THREE.CanvasTexture } | null = null;
  private returnIncomingBulk = 0;
  /**
   * Flash-reduction mirror (LOCKED-DESIGN 12.3/18.5): the Engine feeds its
   * flashReduced() policy (knob OR prefers-reduced-motion) here each frame.
   * Telegraphs are fairness cues and never disappear under reduction — the
   * strobes become steady/soft glows instead (the fuse-halo precedent).
   */
  private flashReduced = false;
  /**
   * Camera-proximity fade on the bulkhead haze (LOCKED-DESIGN 18.10 4B
   * capture: the glass goes milky when the camera is within ~2 units). A 0..1
   * multiplier on the pane tint / smudge overlay / reflection sheen, driven by
   * updateGlassProximity each frame; 1 (full treatment) without an Engine —
   * the editor preview keeps today's look.
   */
  private glassFade = 1;
  private glassFaceZ: number | null = null;
  private readonly glassSheenFx: Array<{ material: THREE.MeshBasicMaterial; baseOpacity: number }> = [];
  /**
   * Route-freshness sign chips (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood):
   * small FRESH/REPEAT/STALE + Grade-delta chips riding the existing
   * accessibility sign chips — the committed route's receiver plus the aimed
   * candidate target. Presentation-only sprites: NO `identifier`/`label`
   * userData, so the QA label/target seams never see them.
   */
  private readonly freshnessChips = new Map<string, {
    sprite: THREE.Sprite;
    canvas: HTMLCanvasElement;
    texture: THREE.CanvasTexture;
    signature: string;
    target: AuthoredTarget;
    enabled: boolean;
  }>();

  // Visuals only: simulation colliders are built from the same definition by ArenaPhysics.ts.
  constructor(definition: ArenaDefinition = ARENA_DEFINITION) {
    this.definition = parseArenaDefinition(definition);
    for (const piece of this.definition.pieces) this.buildPiece(piece);
    this.railObject = this.objects.get("anchor-rail") ?? null;
    this.shutters = { left: this.objects.get("shutter-left") ?? null, right: this.objects.get("shutter-right") ?? null };
    this.baffle = this.objects.get("hazard-baffle") ?? null;
    this.buildLaneShutterFx();
    this.buildEnclosure();
    this.buildGlassBulkhead();
    this.buildSurfaceSignage();
    this.buildReturnAssembly();
  }

  /**
   * Sealed-room enclosure (LOCKED-DESIGN 18.1 "enclosed ceiling and walls").
   * These MESHES stay non-collidable and unregistered in objects/authored/
   * aimTargets: ArenaPhysics derives colliders from the arena definition alone,
   * so none of this touches the simulation or the QA seams (labels are sprites;
   * targets come from `authored`). The side/far walls are no longer open air
   * though — ArenaPhysics.containmentBodies derives four 19 m slabs flush
   * behind these faces from the same floor plan + ENCLOSURE_INTERIOR_HEIGHT
   * (PLAYTEST F15: the 2.5 m curb alone let a run+jump mount its crown and walk
   * off the world). The follow camera (y≈14.5, pitched down) never looks
   * above horizontal, so the tall far/side walls fill the frame's top and side
   * bands where the old "neon void" showed through; the ceiling + trusses cap
   * the box for the chamber view and the editor game-view.
   */
  private buildEnclosure(): void {
    // Interior height to the ceiling deck. The follow camera rides at
    // y ≈ 15.5 (player y + authored offset 14.5), and every overhead piece
    // hangs off H (deck H+0.1, trusses H−0.4, lamp housings H−0.7): at the
    // old 15.6 the camera flew INSIDE that layer and fixtures/deck edges
    // sliced the frustum on the 40-deep floor. Keep the lowest-hanging
    // overhead geometry ≥ 2.5 above the camera band. The value itself lives in
    // ArenaDefinition (ENCLOSURE_INTERIOR_HEIGHT) because the F6 camera clamp
    // also has to know the ceiling — one owner, no second generation of 19s.
    const H = ENCLOSURE_INTERIOR_HEIGHT;
    // LOCKED-DESIGN 18.13 (56 × 40 arena) + PLAN D8: the shell extents DERIVE
    // from the authored floor plan (width W, depth D) instead of a third
    // generation of hardcodes — re-authoring the floor re-sizes the shell.
    // Only the sign/fixture/truss rhythm stays hand-tuned.
    const floor = this.definition.pieces.find((candidate) => candidate.id === "arena-floor");
    const [W, , D] = (floor?.size as Vec3 | undefined) ?? [56, 1, 40];
    const steelMat = (theme: { color: number; roughness: number; metalness: number }, repeatX: number, repeatY: number) => {
      const map = makeSteelTexture(); map.repeat.set(repeatX, repeatY);
      return new THREE.MeshStandardMaterial({ color: theme.color, map, roughness: theme.roughness, metalness: theme.metalness });
    };
    const flatMat = (theme: { color: number; roughness: number; metalness: number }) =>
      new THREE.MeshStandardMaterial({ color: theme.color, roughness: theme.roughness, metalness: theme.metalness });
    const addShell = (name: string, geometry: THREE.BufferGeometry, material: THREE.Material, pos: Vec3, receive = true): THREE.Mesh => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = name; mesh.position.fromArray(pos);
      // never a shadow caster (would block the single key light) and no
      // arenaPieceId, so QA seams that key on pieces/labels never see it
      mesh.castShadow = false; mesh.receiveShadow = receive;
      this.group.add(mesh);
      return mesh;
    };

    // --- structural shell: tall painted-steel far + side walls ---
    // Gap/thickness come from ArenaPhysics (ENCLOSURE_SHELL) because the F15
    // containment colliders sit flush behind these faces: the drawn room and
    // the solid room derive from one owner, so a shell nudge can never re-open
    // the escape hatch the 2.5 m curb alone used to leave.
    const { gap: SG, thickness: ST } = ENCLOSURE_SHELL;
    // The north shell sits 0.3 behind the authored north wall's outer face
    // (wall center -D/2, thickness 1 -> outer face -D/2 - 0.5 -> shell center
    // -D/2 - 0.8); its shoulders read behind the chamber block per 18.13.
    addShell("enclosure-wall-north", new THREE.BoxGeometry(W + 3, H, ST), steelMat(ENCLOSURE.wall, 18, 5), [0, H / 2, -D / 2 - SG]);
    for (const sign of [-1, 1] as const) {
      const side = sign < 0 ? "left" : "right";
      // inner face at x=±28.5 — flush against, never overlapping, the authored
      // curb walls (x=±28, half-width .5), so there is no collider-shadowing
      // z-fight and the low authored wall reads as a wainscot below the ledge
      addShell(`enclosure-wall-${side}`, new THREE.BoxGeometry(ST, H, D + 3), steelMat(ENCLOSURE.wallUpper, 10, 5), [sign * (W / 2 + SG), H / 2, 0]);
      addShell(`enclosure-ledge-${side}`, new THREE.BoxGeometry(1.0, 0.16, D + 2), flatMat(ENCLOSURE.truss), [sign * (W / 2), 2.55, 0]);
    }

    // --- ceiling deck + structural trusses (wide/editor framings) ---
    addShell("enclosure-ceiling", new THREE.BoxGeometry(W + 3, 0.5, D + 4), flatMat(ENCLOSURE.ceiling), [0, H + 0.1, 0], false);
    const trussMat = flatMat(ENCLOSURE.truss);
    // E-W runs on a 6 m rhythm across the 40-deep room, half-phased (±3/±9/±15)
    // so no row sits at z 18 — the follow camera rides at z ≈ 19 from spawn and
    // a row there crosses the camera→chamber sightline (hazard-receiver
    // legibility QA); N-S runs at 12 m across the 56 span so the truss grid
    // stays even, not clustered center.
    for (const z of [-15, -9, -3, 3, 9, 15]) addShell(`enclosure-truss-x${z}`, new THREE.BoxGeometry(W + 2, 0.5, 0.5), trussMat, [0, H - 0.4, z], false);
    for (const x of [-24, -12, 0, 12, 24]) addShell(`enclosure-truss-z${x}`, new THREE.BoxGeometry(0.5, 0.4, D + 2), trussMat, [x, H - 0.28, 0], false);

    // --- ceiling light-fixture practicals: emissive lamp faces in dark housings ---
    // 14 cheap emissive practicals gridded across the 56 × 40 room keep the
    // clean-industrial wash reading at depth (11 -> 14; still no extra shadows).
    const housingMat = flatMat(ENCLOSURE.fixture);
    const lampMat = new THREE.MeshStandardMaterial({ color: ENCLOSURE.lamp.color, emissive: ENCLOSURE.lamp.emissive, emissiveIntensity: ENCLOSURE.lamp.emissiveIntensity, roughness: 0.4 });
    for (const [fx, fz] of [[-21, -15], [-7, -15], [7, -15], [21, -15], [-14, -6], [0, -6], [14, -6], [-21, 3], [-7, 3], [7, 3], [21, 3], [-14, 12], [0, 12], [14, 12]] as const) {
      addShell(`enclosure-fixture-${fx}_${fz}`, new THREE.BoxGeometry(3.4, 0.4, 1.3), housingMat, [fx, H - 0.7, fz], false);
      addShell(`enclosure-lamp-${fx}_${fz}`, new THREE.BoxGeometry(3.0, 0.12, 1.0), lampMat, [fx, H - 0.92, fz], false);
    }
    // one gentle non-shadow ceiling practical shaping the mid lanes / island —
    // range lifted (38->44) and recentered so the single soft fill still
    // reaches the deepened floor without adding a shadow pass.
    const practical = new THREE.PointLight(ENCLOSURE.lamp.emissive, 6, 44, 1.5);
    practical.position.set(0, 11, 0); practical.castShadow = false; practical.name = "enclosure-practical";
    this.group.add(practical);

    // --- painted safety-graphic zones (deadpan corporate signage, 18.1/18.2) ---
    // Side boards ride the side walls (x=±28.46, just inside the inner face)
    // at z=12, clear of BOTH wall-mounted side chutes (18.13: chute-left z 0,
    // chute-right z -6, mouths flush on the ±27.5 inner faces).
    const signMat = flatMat(ENCLOSURE.signage);
    const chevMat = flatMat(ENCLOSURE.chevron);
    for (const sign of [-1, 1] as const) {
      addShell(`enclosure-sign-side-${sign}`, new THREE.BoxGeometry(0.08, 1.9, 3.4), signMat, [sign * (W / 2 + 0.46), 4.6, 12]);
      addShell(`enclosure-sign-side-border-${sign}`, new THREE.BoxGeometry(0.05, 2.3, 3.8), chevMat, [sign * (W / 2 + 0.49), 4.6, 12]);
    }
    // North sign rides the shell face above the chamber-block roof (y 6.6 >
    // ceiling top 5.0), 0.06 proud of the shell inner face at -D/2 - 0.5.
    addShell("enclosure-sign-north", new THREE.BoxGeometry(6, 1.1, 0.08), signMat, [0, 6.6, -D / 2 - 0.44]);
    addShell("enclosure-sign-north-border", new THREE.BoxGeometry(6.4, 1.5, 0.05), chevMat, [0, 6.6, -D / 2 - 0.47]);
  }

  /**
   * Reinforced-glass presence (LOCKED-DESIGN 2.1 broad glass bulkhead, 18.1
   * "reinforced glass with real physical presence"). Visual-only detail raised
   * around the authored `glass-bulkhead` pane so the story-critical sealed
   * barrier reads as a heavy window, not a flat tint: a structural steel
   * mullion/transom grid + heavy perimeter frame, a laminated edge tint and
   * seeded smudge/impact overlay (the enforced glass beat lands here — the
   * overlay is wired into glassFx), and a baked reflection sheen (quality-tiered
   * by setQuality; there is NO per-frame planar reflection on any tier). Every
   * mesh carries `arenaPieceId = "glass-bulkhead"` so it is transparent to the
   * sightline QA (which skips `glass-*` pieces) and never registered as an
   * occluder — the chamber machinery stays visible through it.
   */
  private buildGlassBulkhead(): void {
    const piece = this.definition.pieces.find((candidate) => candidate.id === "glass-bulkhead");
    if (!piece) return;
    const [w, h] = piece.size as Vec3;
    const [px, py, pz] = piece.position;
    const faceZ = pz + (piece.size as Vec3)[2] / 2; // arena-facing (+z, toward camera)
    this.glassFaceZ = faceZ; // camera-proximity haze fade anchor (18.10 4B)
    const add = (mesh: THREE.Mesh): THREE.Mesh => {
      mesh.userData.arenaPieceId = "glass-bulkhead";
      mesh.castShadow = false; mesh.receiveShadow = false;
      this.group.add(mesh);
      return mesh;
    };

    // --- structural steel: heavy perimeter frame + horizontal transoms ---
    const steel = new THREE.MeshStandardMaterial({ color: GLASS.frame.color, roughness: GLASS.frame.roughness, metalness: GLASS.frame.metalness });
    const beam = (sx: number, sy: number, x: number, y: number): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, .34), steel);
      m.position.set(px + x, py + y, faceZ - .12); m.name = "glass-frame-beam";
      return add(m);
    };
    beam(w + .5, .42, 0, h / 2 + .12); beam(w + .5, .42, 0, -h / 2 - .12); // head + sill
    beam(.4, h + .5, w / 2 + .1, 0); beam(.4, h + .5, -w / 2 - .1, 0);     // jambs
    // transoms at the quarter heights (clear of the y=2.75 hazard sightline)
    beam(w, .2, 0, h * .25); beam(w, .2, 0, -h * .25);

    // --- laminated edge tint + seeded smudge / impact overlay (glass beat) ---
    const { overlay, sheen } = makeGlassTextures();
    const overlayMat = new THREE.MeshBasicMaterial({ map: overlay, transparent: true, opacity: GLASS.overlay.opacity, depthWrite: false });
    const overlayMesh = new THREE.Mesh(new THREE.PlaneGeometry(w - .1, h - .1), overlayMat);
    overlayMesh.position.set(px, py, faceZ + .02); overlayMesh.name = "glass-smudge-overlay";
    overlayMesh.renderOrder = 2; add(overlayMesh);
    this.glassFx.push({ material: overlayMat, baseOpacity: GLASS.overlay.opacity });

    // --- baked reflection sheen (additive, dropped on the low tier) ---
    const sheenMat = new THREE.MeshBasicMaterial({ map: sheen, transparent: true, opacity: GLASS.sheen.opacity, depthWrite: false, blending: THREE.AdditiveBlending });
    const sheenMesh = new THREE.Mesh(new THREE.PlaneGeometry(w - .1, h - .1), sheenMat);
    sheenMesh.position.set(px, py, faceZ + .03); sheenMesh.name = "glass-sheen";
    sheenMesh.renderOrder = 3; add(sheenMesh);
    this.glassSheenMeshes.push(sheenMesh);
    this.glassSheenFx.push({ material: sheenMat, baseOpacity: GLASS.sheen.opacity });
  }

  /**
   * Quality-tier hook for the glass reflection (LOCKED-DESIGN 13.1). The
   * reflection is a STATIC envMap read plus a baked additive sheen — no
   * per-frame planar reflection exists on any tier — so tiering only drops the
   * sheen mesh and softens the envMap read on the low tier. Visual-only; the
   * simulation never sees it (Engine.setQuality routes here).
   */
  setQuality(level: QualityLevel): void {
    this.qualityLevel = level;
    const high = level === "high" || level === "medium";
    for (const mesh of this.glassSheenMeshes) mesh.visible = high;
    for (const mat of this.glassPaneMats) {
      mat.envMapIntensity = high ? GLASS.pane.envMapIntensity : GLASS.pane.envMapIntensityLow;
      mat.needsUpdate = true;
    }
    // hero machinery garnish (grates, fins, cage rings, heat shimmer) drops on
    // the low tier (13.1) — structure meshes never toggle, so silhouettes and
    // the QA seams read identically on every tier
    applyHeroQuality(this.group, level);
  }

  /**
   * Retire this instance: any still-in-flight GLTF-upgrade probe disposes its
   * asset instead of mounting into a dead scene graph (see `disposed`).
   * Owners (Engine.destroy, the editor's per-edit rebuild) call this BEFORE
   * dropping their reference; hero geometry/materials are released here, the
   * rest of the group stays the owner's to tear down (idempotent either way).
   */
  dispose(): void {
    this.disposed = true;
    for (const asset of this.heroMounts.values()) asset.dispose();
    this.heroMounts.clear();
  }

  /**
   * Diegetic on-surface safety graphics (LOCKED-DESIGN 18.1/18.2). Painted
   * facility markings on the ACTUAL floor so the identifiers read as part of the
   * room, not HUD: worn blue registration brackets around each floor-level
   * anchor bay (the "painted floor markings" of 18.1), plus a stencilled
   * multiplier plate for EVERY receiver tier — the ×1.0/×1.25/×1.5 + tier FORM
   * painted on each receiver's approach deck — so the multiplier identity now
   * lives diegetically for all three tiers (the migration was safe-only) rather
   * than solely on the slimmed sign face. Visual-only — no arenaPieceId any
   * target/label seam keys on — and deliberately SPARSE: signage supports
   * readability, it does not wallpaper the room (2.2).
   */
  private buildSurfaceSignage(): void {
    const decal = (geometry: THREE.BufferGeometry, material: THREE.Material, pos: Vec3, rot: Vec3, name: string): void => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.fromArray(pos); mesh.rotation.set(...rot);
      mesh.name = name; mesh.castShadow = false; mesh.receiveShadow = false;
      this.group.add(mesh);
    };

    // Painted registration brackets on the floor-level anchor bays (blue paint,
    // flat on the concrete). Only anchors that actually sit on the floor.
    const bracketMat = new THREE.MeshBasicMaterial({ color: PALETTE.anchorBlue, transparent: true, opacity: .42, depthWrite: false });
    for (const id of ["anchor-floor", "anchor-ramp"] as const) {
      const anchor = this.definition.pieces.find((candidate) => candidate.id === id);
      if (!anchor || anchor.position[1] > 1.4) continue;
      const [ax, , az] = anchor.position; const [bw, bh] = anchor.size as Vec3;
      const hx = bw / 2 + .3; const hz = bh / 2 + .3;
      for (const sx of [-1, 1] as const) for (const sz of [-1, 1] as const) {
        decal(new THREE.PlaneGeometry(.85, .14), bracketMat, [ax + sx * hx - sx * .35, .016, az + sz * hz], [-Math.PI / 2, 0, 0], `${id}-mark`);
        decal(new THREE.PlaneGeometry(.14, .85), bracketMat, [ax + sx * hx, .016, az + sz * hz - sz * .35], [-Math.PI / 2, 0, 0], `${id}-mark`);
      }
    }

    // Stencilled tier plate painted flat on the safe receiver's floor lane: the
    // multiplier + tier FORM, deadpan facility paint reinforcing the sign faces.
    const safeLane = this.definition.pieces.find((candidate) => candidate.id === "lane-safe");
    if (safeLane) {
      const plateMat = new THREE.MeshBasicMaterial({ map: makeStencilPlate("SAFE x1.0", "safe"), transparent: true, opacity: .62, depthWrite: false });
      const [lx, ly, lz] = safeLane.position; const size = safeLane.size as Vec3;
      decal(new THREE.PlaneGeometry(2.2, 1.0), plateMat, [lx, ly + size[1] / 2 + .01, lz + size[2] / 2 - .9], [-Math.PI / 2, 0, 0], "tier-plate-safe");
    }
    // Technical + hazard multiplier plates on the chamber deck in front of each
    // receiver (18.1 migration was safe-only): ×1.25 / ×1.5 + tier FORM now read
    // diegetically off the floor, not solely off the slimmed sign face.
    for (const [id, text, glyph] of [
      ["receiver-technical", "TECH x1.25", "technical"],
      ["receiver-hazard", "HAZARD x1.5", "hazard"],
    ] as const) {
      const receiver = this.definition.pieces.find((candidate) => candidate.id === id);
      if (!receiver) continue;
      const plateMat = new THREE.MeshBasicMaterial({ map: makeStencilPlate(text, glyph), transparent: true, opacity: .6, depthWrite: false });
      // Track the receiver's authored x so the multiplier plate stays under its
      // lane at any arena width (18.12: receivers spread to ±7.5; 18.13: the
      // chamber band moved north 6, plates follow to z -14.2).
      decal(new THREE.PlaneGeometry(2.0, 1.0), plateMat, [receiver.position[0], .02, -14.2], [-Math.PI / 2, 0, 0], `tier-plate-${glyph}`);
    }

    // Painted THROW stance decal on the tower landing deck (LOCKED-DESIGN
    // 18.13 D-access / spec §5.1): marks the authored stance whose hold center
    // clears the lip's top edge and drops into the recess throat onto the
    // precision ring (the throat is the gap the recessed `tower-head` face
    // opens above `tower-head-base`; the mouth below the lip is sealed, which
    // is what keeps a ground throw out, and the hood above it — roof visor,
    // raised cheeks/caps, `tower-lip-crown*` — is what keeps a mid-air one
    // out). Derives from tower-top so an editor nudge keeps the mark on deck.
    const landing = this.definition.pieces.find((candidate) => candidate.id === "tower-top");
    if (landing) {
      const deckTop = landing.position[1] + (landing.size as Vec3)[1] / 2;
      const throwMat = new THREE.MeshBasicMaterial({ map: makeStencilPlate("THROW", "anchor"), transparent: true, opacity: .62, depthWrite: false });
      decal(new THREE.PlaneGeometry(1.8, 0.9), throwMat, [landing.position[0], deckTop + .012, -1.35], [-Math.PI / 2, 0, 0], "tower-landing-throw");
    }
  }

  /**
   * Reject-return conveyance (LOCKED-DESIGN Section 6 / 18.10 third dogfood):
   * the reject load's journey back to the arena must be a followable physical
   * event, so the route gets real furniture — a visible collection belt along
   * the chamber deck (behind the glass, in front of the receiver lanes)
   * feeding a ducted hood at its east end, and the locked "one bright, fixed
   * chute beside the sorting island" realized as a lit floor hatch where
   * returned pieces erupt back onto the floor. A status plate above the hatch
   * ("RETURNING · N BULK") plus the teal collar/belt glow light only while a
   * return is incoming (setReturnIncoming). Visual-only, enclosure-style: no
   * colliders, no objects/authored registration, no identifier userData — the
   * QA target/label seams never see any of it.
   */
  private buildReturnAssembly(): void {
    const add = (mesh: THREE.Mesh, name: string): THREE.Mesh => {
      mesh.name = name; mesh.castShadow = false; mesh.receiveShadow = true;
      this.group.add(mesh);
      return mesh;
    };
    const steel = new THREE.MeshStandardMaterial(RETURN_ASSEMBLY.steel);
    const dark = new THREE.MeshStandardMaterial(RETURN_ASSEMBLY.dark);

    // --- chamber collection belt: glass-side tray running to the east duct ---
    const beltTexture = makeConveyorTexture();
    beltTexture.repeat.set(1, 5.2);
    const beltMat = new THREE.MeshStandardMaterial({
      ...ARENA_TEXTURED_PRESETS.conveyor, map: beltTexture, emissiveMap: beltTexture,
    });
    const belt = new THREE.Mesh(new THREE.PlaneGeometry(0.56, 12.2), beltMat);
    belt.position.set(0.8, 0.43, -13.62);
    belt.rotation.set(-Math.PI / 2, 0, Math.PI / 2); // lay flat, chevrons along x
    add(belt, "return-belt");
    this.returnBeltFx = { material: beltMat, texture: beltTexture, baseEmissive: beltMat.emissiveIntensity };
    const tray = new THREE.Mesh(new THREE.BoxGeometry(12.4, 0.34, 0.62), steel);
    tray.position.set(0.8, 0.22, -13.62);
    add(tray, "return-belt-tray");

    // --- east duct hood: where the belt hands rejects to the under-floor run ---
    const hood = new THREE.Mesh(new THREE.BoxGeometry(1.25, 1.1, 1.05), steel);
    hood.position.set(7.35, 0.55, -13.62);
    add(hood, "return-duct-hood");
    const mouth = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 0.85), dark);
    mouth.position.set(6.71, 0.55, -13.62);
    mouth.rotation.y = -Math.PI / 2;
    add(mouth, "return-duct-mouth");

    // --- return hatch beside the sorting island (the bright, fixed chute) ---
    const collarMat = new THREE.MeshStandardMaterial(RETURN_ASSEMBLY.collar);
    this.returnCollarMat = collarMat;
    this.returnCollarBaseEmissive = collarMat.emissiveIntensity;
    // beside the sorting island (18.13: island platform at (0, 0.55, -5.3))
    const HATCH: Vec3 = [4.8, 0, -5.5];
    for (const [sx, sz, w, d] of [[0, .85, 1.7, .12], [0, -.85, 1.7, .12], [.85, 0, .12, 1.7], [-.85, 0, .12, 1.7]] as const) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(w, 0.1, d), collarMat);
      bar.position.set(HATCH[0] + sx, 0.06, HATCH[2] + sz);
      add(bar, "return-hatch-collar");
    }
    const grate = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.05, 1.5), dark);
    grate.position.set(HATCH[0], 0.02, HATCH[2]);
    add(grate, "return-hatch-grate");
    // painted stencil marking on the floor east of the hatch (deadpan signage)
    const stencilMat = new THREE.MeshBasicMaterial({ map: makeStencilPlate("REJECT RETURN", "infeed"), transparent: true, opacity: .6, depthWrite: false });
    const stencil = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.1), stencilMat);
    stencil.position.set(6.6, 0.016, -5.5);
    stencil.rotation.set(-Math.PI / 2, 0, 0);
    stencil.castShadow = stencil.receiveShadow = false;
    stencil.name = "return-hatch-stencil";
    this.group.add(stencil);

    // --- incoming-return status plate (canvas sprite, redrawn only on change) ---
    const canvas = document.createElement("canvas");
    canvas.width = 448; canvas.height = 84;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false }));
    sprite.name = "return-hatch-plate";
    sprite.renderOrder = 30;
    sprite.scale.set(2.7, 0.5, 1);
    sprite.position.set(HATCH[0], 2.3, HATCH[2]);
    sprite.visible = false;
    this.group.add(sprite);
    this.returnPlate = { sprite, canvas, texture };
  }

  /** Warning strips + sweep bookkeeping for the authored lane shutters. */
  private buildLaneShutterFx(): void {
    for (const side of ["left", "right"] as const) {
      const object = this.shutters[side];
      const piece = this.definition.pieces.find((candidate) => candidate.id === `shutter-${side}`);
      if (!object || !piece) continue;
      const size = piece.size as Vec3;
      // flashing edge light marking the sweep footprint (visible only while warning/sweeping)
      const stripMat = new THREE.MeshBasicMaterial({ color: FEEDBACK_COLORS.telegraph, transparent: true, opacity: 0.8, depthWrite: false });
      const strip = new THREE.Mesh(new THREE.BoxGeometry(size[0] * 0.9, 0.07, size[2]), stripMat);
      strip.name = `shutter-warn-strip-${side}`;
      strip.userData.arenaPieceId = piece.id;
      strip.position.set(piece.position[0], piece.position[1] + size[1] / 2 + 0.08, piece.position[2]);
      strip.visible = false;
      this.group.add(strip);
      this.laneShutterFx.set(side, {
        object,
        strip,
        stripMat,
        baseZ: piece.position[2],
        length: size[2],
        progress: object.visible ? 1 : 0,
        targetClosed: object.visible,
      });
    }
  }

  portalTarget(object: THREE.Object3D, endpoint: "A" | "B"): AuthoredTarget | null {
    const target = this.authored.get(object);
    return target?.endpoint === endpoint ? target : null;
  }

  /**
   * QA/editor label-detection seam: every screen-readable identifier object,
   * keyed by `userData.label` (stable semantic id). Phase 4A migrated the route /
   * multiplier / infeed identity onto diegetic surface graphics (painted floor
   * brackets + stencilled multiplier plates, see buildSurfaceSignage) and slimmed
   * the surviving label layer into a compact camera-facing accessibility chip
   * (see label()) — the sanctioned accessibility/QA overlay (18.1), tagged
   * `userData.identifier`, which is what this seam keys on (tech-agnostic: a
   * future decal/mesh identifier only needs the same flag + `userData.label` + a
   * screenRect anchor to stay detected). The painted on-surface markings carry
   * the diegetic identity but are deliberately NOT tagged identifiers, so they
   * never spoof the "visible identifier per target" check — the always-legible
   * chip does.
   */
  labelObjects(): THREE.Object3D[] {
    return this.group.children.filter((object) => object.userData.identifier === true);
  }

  /**
   * Honest screen-space rect in pixels for readability review. Sprites are
   * billboarded quads whose world scale IS their size in the camera plane;
   * every other object projects the corners of its true world-space geometry
   * bounds (bounding box × world matrix), so unit-scale meshes with baked-in
   * vertex sizes measure what the player actually sees.
   */
  screenRect(object: THREE.Object3D, camera: THREE.Camera, width: number, height: number): { x: number; y: number; width: number; height: number } {
    return this.screenRectInto(object, camera, width, height, { x: 0, y: 0, width: 0, height: 0 });
  }

  /**
   * screenRect's allocation-free core: writes into `out` using the pooled
   * probe vectors, so the per-frame declutter pass can measure every visible
   * label without churning Vector3s. The public screenRect seam above keeps
   * returning a fresh rect for the QA/editor callers.
   */
  private screenRectInto(
    object: THREE.Object3D, camera: THREE.Camera, width: number, height: number,
    out: { x: number; y: number; width: number; height: number },
  ): { x: number; y: number; width: number; height: number } {
    object.updateWorldMatrix(true, true);
    const center = object.getWorldPosition(this.rectCenter);
    if ((object as THREE.Sprite).isSprite) {
      const projected = this.rectProjected.copy(center).project(camera);
      const matrix = camera.matrixWorld.elements;
      const axis = (right: boolean, half: number) => {
        const probe = this.rectProbe.copy(center);
        const offset = right ? 0 : 4;
        probe.x += matrix[offset]! * half; probe.y += matrix[offset + 1]! * half; probe.z += matrix[offset + 2]! * half;
        probe.project(camera);
        return right ? Math.abs(probe.x - projected.x) * width * 2 : Math.abs(probe.y - projected.y) * height * 2;
      };
      out.x = (projected.x * .5 + .5) * width;
      out.y = (-projected.y * .5 + .5) * height;
      out.width = axis(true, object.scale.x / 2);
      out.height = axis(false, object.scale.y / 2);
      return out;
    }
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    const corner = this.rectCorner;
    object.traverse((node) => {
      const geometry = (node as THREE.Mesh).geometry;
      if (!geometry) return;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const box = geometry.boundingBox!;
      for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
        corner.set(x, y, z).applyMatrix4(node.matrixWorld).project(camera);
        const sx = (corner.x * .5 + .5) * width;
        const sy = (-corner.y * .5 + .5) * height;
        minX = Math.min(minX, sx); maxX = Math.max(maxX, sx);
        minY = Math.min(minY, sy); maxY = Math.max(maxY, sy);
      }
    });
    if (minX > maxX) {
      const projected = center.project(camera);
      out.x = (projected.x * .5 + .5) * width;
      out.y = (-projected.y * .5 + .5) * height;
      out.width = 0; out.height = 0;
      return out;
    }
    out.x = (minX + maxX) / 2;
    out.y = (minY + maxY) / 2;
    out.width = maxX - minX;
    out.height = maxY - minY;
    return out;
  }

  animate(t: number, dt = 0): void {
    const flicker = 1.2 + .35 * Math.sin(t * 9.3) + .22 * Math.sin(t * 23.7 + 1.4);
    if (this.fireMat) this.fireMat.emissiveIntensity = flicker;
    if (this.fireLight) this.fireLight.intensity = 12 + flicker * 5;
    // furnace-maw heat shimmer (18.3): a slow rising scroll — motion, not a
    // strobe, so no flash-reduction branch is needed; the low tier hides the
    // sheet entirely (applyHeroQuality) and skips the work here.
    if (this.furnaceShimmer && this.furnaceShimmer.mesh.visible) {
      this.furnaceShimmer.texture.offset.y = (this.furnaceShimmer.texture.offset.y - dt * 0.42) % 1;
      this.furnaceShimmer.material.opacity = HERO_THEME.furnace.shimmer.opacity * (0.8 + 0.12 * Math.sin(t * 2.3));
    }

    // reward flash decay (receiver payoff pulses read via applyTargetVisualState)
    for (const lane of ["safe", "technical", "hazard"] as const) {
      if (this.receiverPulseLevels[lane] > 0) this.receiverPulseLevels[lane] = Math.max(0, this.receiverPulseLevels[lane] - dt * 3);
    }

    // mini-batch chute telegraphs: pulse the mouth signal strip at ~2.9Hz —
    // under the 3 flashes/sec photosensitivity limit (up to three strips can
    // strobe at once). Flash reduction holds the cue lit with no pulse at all.
    for (const [lane, fx] of this.chuteFx) {
      const pulse = this.chuteTelegraphActive[lane]
        ? this.flashReduced ? 0.75 : 0.5 + 0.5 * Math.sin(t * 18)
        : 0;
      fx.signal.emissiveIntensity = fx.baseEmissive + pulse * 1.7;
    }

    // critical-overflow chute shutters: visible slam closed, slide open on clear
    const shutterTarget = this.chuteShuttersClosed ? 1 : 0;
    if (this.chuteShutterProgress !== shutterTarget) {
      const rate = dt / (this.chuteShuttersClosed ? 0.35 : 0.8); // slam fast, reopen slow
      this.chuteShutterProgress += Math.max(-rate, Math.min(rate, shutterTarget - this.chuteShutterProgress));
      if (Math.abs(this.chuteShutterProgress - shutterTarget) < 0.005) this.chuteShutterProgress = shutterTarget;
    }
    for (const [, fx] of this.chuteFx) {
      const progress = this.chuteShutterProgress;
      fx.shutter.visible = progress > 0.02;
      fx.shutter.position.y = fx.openY + (fx.closedY - fx.openY) * progress;
      fx.shutterMat.emissiveIntensity = progress * (this.flashReduced ? 0.5 : 0.35 + 0.35 * Math.max(0, Math.sin(t * 10)));
    }

    // lane shutters: 5s warning strip flash, then a visible sweep along the lane
    for (const [side, fx] of this.laneShutterFx) {
      const target = fx.targetClosed ? 1 : 0;
      if (fx.progress !== target) {
        const rate = dt / 1.6; // slow, unstoppable safety sweep (Section 2.1)
        fx.progress += Math.max(-rate, Math.min(rate, target - fx.progress));
        if (Math.abs(fx.progress - target) < 0.004) fx.progress = target;
      }
      const sweeping = fx.progress > 0.01 && fx.progress < 0.999;
      fx.object.visible = fx.progress > 0.01;
      fx.object.position.z = fx.baseZ - (1 - fx.progress) * fx.length;
      const warning = this.shutterWarnLane === side;
      fx.strip.visible = warning || sweeping || fx.progress >= 0.999;
      fx.strip.position.z = fx.object.visible ? fx.object.position.z : fx.baseZ;
      // hard ~2.7Hz blink is the locked warning (kept under the 3 flashes/sec
      // safety limit); under flash reduction the strip holds a steady bright
      // state (the cue survives, the strobe does not)
      fx.stripMat.opacity = warning
        ? this.flashReduced ? 0.85 : (Math.sin(t * 17) > 0 ? 0.9 : 0.15)
        : sweeping ? 0.85 : 0.4;
    }

    // conveyor-reversal warning: chevrons scroll toward the UPCOMING direction
    for (const fx of this.conveyorFx) {
      if (this.conveyorWarnDirection !== 0) {
        // the scroll IS the direction cue and stays; only the strobe is capped
        fx.texture.offset.y = (fx.texture.offset.y + this.conveyorWarnDirection * dt * 0.9) % 1;
        fx.material.emissiveIntensity = fx.baseEmissive + (this.flashReduced ? 0.5 : 0.5 + 0.5 * Math.sin(t * 14)) * 0.3;
      } else if (fx.material.emissiveIntensity !== fx.baseEmissive) {
        fx.material.emissiveIntensity = fx.baseEmissive;
        fx.texture.offset.y = 0;
      }
    }

    // contained chamber blast: reinforced-glass stress shimmer, no arena
    // shockwave. The bulkhead fills much of the chamber frame, so the shimmer
    // is capped at ~2.9Hz (|sin| doubles the frequency) — never the 5-30Hz
    // photosensitive band. Flash reduction turns it into a steady swell.
    // Every opacity is scaled by the camera-proximity glassFade (18.10 4B:
    // the haze thins as the camera closes so the chamber stays legible).
    if (this.glassStressLevel > 0) {
      this.glassStressLevel = Math.max(0, this.glassStressLevel - dt / 0.7);
      const stress = this.glassStressLevel;
      for (const fx of this.glassFx) {
        fx.material.opacity = (fx.baseOpacity + stress * (this.flashReduced ? 0.14 : 0.1 + 0.16 * Math.abs(Math.sin(t * 9)))) * this.glassFade;
      }
    } else {
      for (const fx of this.glassFx) {
        const faded = fx.baseOpacity * this.glassFade;
        if (fx.material.opacity !== faded) fx.material.opacity = faded;
      }
    }
    for (const fx of this.glassSheenFx) {
      const faded = fx.baseOpacity * this.glassFade;
      if (fx.material.opacity !== faded) fx.material.opacity = faded;
    }

    // expiring-panel five-second warning (18.4): a panel rotating out at the
    // next phase boundary blinks its own light (~2.7Hz, under the flash-safety
    // limit) while its identifier breathes; flash reduction holds a steady
    // raised glow with no strobe and no motion. updateGameplay/updateOccupied
    // rebase the material state every frame BEFORE animate(), so the additive
    // blink never accumulates.
    for (const target of this.authored.values()) {
      const label = target.label;
      if (label.userData.baseScaleX === undefined) {
        label.userData.baseScaleX = label.scale.x;
        label.userData.baseScaleY = label.scale.y;
      }
      const expiring = this.expiringPanels.has(`${target.endpoint}:${target.name}`);
      // Jam invite (18.16): the receiver the recovery prompt names. Never
      // competes with an expiring warning — that one carries a deadline.
      const inviting = !expiring && target.endpoint === "B" && target.name === this.inviteReceiver;
      if (expiring || inviting) {
        const blink = expiring
          ? this.flashReduced ? 0.4 : (Math.sin(t * 17) > 0 ? 0.65 : 0.06)
          : 0.55; // invite: steady raise, no strobe in either flash policy
        const updated = new Set<THREE.Material>();
        for (const visual of target.visuals) {
          const material = (visual as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
          if (!material || updated.has(material) || !("emissiveIntensity" in material)) continue;
          updated.add(material);
          material.emissiveIntensity += blink;
        }
        const breathe = this.flashReduced ? 1 : 1 + (expiring ? 0.05 * Math.sin(t * 8.5) : 0.045 * Math.sin(t * 4.2));
        label.scale.set(label.userData.baseScaleX * breathe, label.userData.baseScaleY * breathe, 1);
      } else if (label.scale.x !== label.userData.baseScaleX) {
        label.scale.set(label.userData.baseScaleX, label.userData.baseScaleY, 1);
      }
    }

    // Maw FEED GUARD glow (LOCKED-DESIGN 18.12): the re-dressed handle glows teal
    // while any lane is jammed so the world matches the recovery prompt. ~0.8Hz
    // swell (flash-safe); flash reduction holds a steady raised glow.
    if (this.recoveryHandleMat) {
      if (this.jamActive) {
        const pulse = this.flashReduced ? 1 : 0.6 + 0.4 * Math.max(0, Math.sin(t * 5));
        this.recoveryHandleMat.emissiveIntensity = this.recoveryHandleBaseEmissive + 0.8 + pulse * 1.2;
      } else if (this.recoveryHandleMat.emissiveIntensity !== this.recoveryHandleBaseEmissive) {
        this.recoveryHandleMat.emissiveIntensity = this.recoveryHandleBaseEmissive;
      }
    }

    // CHAMBER HEAT (LOCKED-DESIGN 18.12 stage 3, dwell meter world half): the
    // furnace emissive + fire light intensity scale with the dwell fill so the
    // chamber visibly heats up as the player loiters. Motion-free ramp — no strobe,
    // so no flash-reduction branch needed.
    if (this.chamberHeat > 0) {
      if (this.fireMat) this.fireMat.emissiveIntensity = flicker * (1 + this.chamberHeat * 1.6);
      if (this.fireLight) this.fireLight.intensity = (12 + flicker * 5) * (1 + this.chamberHeat * 1.1);
    }

    // FURNACE OVERLOAD distress (LOCKED-DESIGN 18.12): the reinforced-glass window
    // glow ramps amber→red across the bulkhead as the overload clock runs down
    // (seen from the arena). Steady ramp, never a strobe.
    if (this.furnaceDistress > 0) {
      for (const fx of this.glassFx) {
        fx.material.opacity = Math.max(fx.material.opacity, (fx.baseOpacity + this.furnaceDistress * 0.22) * this.glassFade);
      }
    }

    // RETURN PAD spin-up ring fill (LOCKED-DESIGN 18.12 §3.4): the pad brightens
    // as the launch charges, snapping bright at full charge.
    if (this.returnPadMat) {
      const target = this.returnPadBaseEmissive + this.returnPadCharge * 2.4;
      if (this.returnPadMat.emissiveIntensity !== target) this.returnPadMat.emissiveIntensity = target;
    }

    // reject-return conveyance (Section 6/18.10): while a return is incoming
    // the collection belt scrolls toward the east duct and the hatch collar
    // holds a raised teal glow (a steady swell under flash reduction — the
    // cue is fairness-relevant and never strobes hard).
    if (this.returnBeltFx) {
      const fx = this.returnBeltFx;
      if (this.returnIncomingBulk > 0) {
        fx.texture.offset.y = (fx.texture.offset.y - dt * 0.9) % 1;
        fx.material.emissiveIntensity = fx.baseEmissive + (this.flashReduced ? 0.3 : 0.22 + 0.14 * Math.sin(t * 5.6));
      } else if (fx.material.emissiveIntensity !== fx.baseEmissive) {
        fx.material.emissiveIntensity = fx.baseEmissive;
      }
    }
    if (this.returnCollarMat) {
      if (this.returnIncomingBulk > 0) {
        const swell = this.flashReduced ? 1 : 0.7 + 0.3 * Math.sin(t * 5.6);
        this.returnCollarMat.emissiveIntensity = this.returnCollarBaseEmissive + 0.9 * swell;
      } else if (this.returnCollarMat.emissiveIntensity !== this.returnCollarBaseEmissive) {
        this.returnCollarMat.emissiveIntensity = this.returnCollarBaseEmissive;
      }
    }

    // hazard-baffle gate lamp: red = throat blocked, green = open, dim = inactive
    if (this.baffleLampMat) {
      const state = this.baffleGateBlocked;
      const color = state === null ? 0x39414d : state ? PALETTE.tierHazard : PALETTE.tierSafe;
      this.baffleLampMat.color.setHex(color);
      this.baffleLampMat.emissive.setHex(color);
      this.baffleLampMat.emissiveIntensity = state === null ? 0.05
        : state ? (this.flashReduced ? 1.1 : 0.9 + 0.4 * Math.max(0, Math.sin(t * 12)))
        : 0.75;
    }
  }

  // ---------- presentation hooks (Engine-facing, LOCKED-DESIGN 18.4) ----------

  /** Clean/partial payoff flash on a receiver lane (decays over ~0.4s). */
  pulseReceiver(lane: ReceiverTier, strength = 1): void {
    this.receiverPulseLevels[lane] = Math.max(this.receiverPulseLevels[lane], strength);
  }

  /** Mini-batch telegraph on a chute mouth (flashing while active). */
  setChuteTelegraph(lane: ChuteId, active: boolean): void {
    this.chuteTelegraphActive[lane] = active;
  }

  /** Critical overflow shutters ALL inbound chutes (Section 7) — visual state only. */
  setChuteShuttersClosed(closed: boolean): void {
    this.chuteShuttersClosed = closed;
  }

  /** Five-second lane-shutter warning (flashing edge lights); null clears. */
  setShutterWarning(lane: "left" | "right" | null): void {
    this.shutterWarnLane = lane;
  }

  /** Five-second conveyor-reversal warning; the cue scrolls toward the NEW flow. */
  setConveyorWarning(direction: "forward" | "reverse" | null): void {
    this.conveyorWarnDirection = direction === null ? 0 : direction === "reverse" ? 1 : -1;
  }

  /**
   * Five-second expiring-panel warning (18.4): keys are `endpoint:name`
   * (`A:wall`, `B:hazard`) for panels active now but rotating out at the next
   * phase boundary. animate() blinks the panel light and breathes its label.
   */
  setExpiringPanels(keys: readonly string[]): void {
    this.expiringPanels.clear();
    for (const key of keys) this.expiringPanels.add(key);
  }

  /** Chamber blast: reinforced-glass stress flicker (Section 5, contained). */
  stressGlass(level = 1): void {
    this.glassStressLevel = Math.max(this.glassStressLevel, Math.min(1, level));
  }

  /** Hazard baffle gate lamp state; null while the hazard receiver is inactive. */
  setBaffleGate(blocked: boolean | null): void {
    this.baffleGateBlocked = blocked;
  }

  /**
   * Any receiver lane jammed (LOCKED-DESIGN 18.12): lights the maw feed guard teal
   * so the recovery affordance (feed the maw) is visibly marked.
   */
  setJamActive(active: boolean): void {
    this.jamActive = active;
  }

  /**
   * Jam INVITE highlight (LOCKED-DESIGN 18.16 / PLAYTEST F10): steadily lights
   * the receiver panel + label the recovery prompt is naming, so "GO CLEAR IT"
   * has a place in the world and not only in the HUD. `null` clears it — the
   * Engine calls that the same frame the jam clears. The Section 3.3 interlock
   * means this is always the JAMMED lane, never a "switch to this one instead"
   * lane: the kernel would refuse that swap (see JamRecoveryCopy).
   */
  setInviteReceiver(lane: ReceiverTier | null): void {
    this.inviteReceiver = lane;
  }

  /** Read-only mirror of the invite highlight (QA asserts the world agrees with the HUD). */
  get invitedReceiver(): ReceiverTier | null {
    return this.inviteReceiver;
  }

  /** CHAMBER HEAT dwell fill (0..1): scales the furnace glow while the player loiters (18.12). */
  setChamberHeat(fill: number): void {
    this.chamberHeat = Math.max(0, Math.min(1, fill));
  }

  /** FURNACE OVERLOAD distress (0..1): ramps the glass-window glow amber→red (18.12). */
  setFurnaceDistress(level: number): void {
    this.furnaceDistress = Math.max(0, Math.min(1, level));
  }

  /** Return-pad spin-up charge (0..1): fills the pad ring during the launch charge (18.12). */
  setReturnPadCharge(charge: number): void {
    this.returnPadCharge = Math.max(0, Math.min(1, charge));
  }

  /**
   * Return-incoming world cue (Section 6 / 18.10): total reject bulk currently
   * headed for the return chute (in the tray or riding the belt). >0 lights the
   * hatch collar, scrolls the collection belt, and shows the "RETURNING · N
   * BULK" plate; 0 puts the assembly to rest. Redraws the plate only on change.
   */
  setReturnIncoming(bulk: number): void {
    const value = Math.max(0, Math.round(bulk));
    if (value === this.returnIncomingBulk) return;
    this.returnIncomingBulk = value;
    const plate = this.returnPlate;
    if (!plate) return;
    plate.sprite.visible = value > 0;
    if (value <= 0) return;
    const ctx = plate.canvas.getContext("2d")!;
    const W = plate.canvas.width; const H = plate.canvas.height;
    const accent = cssHex(PALETTE.conveyance);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = SIGNAGE.plate.body; ctx.beginPath(); ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.fill();
    ctx.strokeStyle = accent; ctx.lineWidth = 3; ctx.beginPath(); ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.stroke();
    drawSignGlyph(ctx, "infeed", 40, H / 2, 20, accent);
    ctx.fillStyle = SIGNAGE.plate.text; ctx.textAlign = "left"; ctx.textBaseline = "middle";
    ctx.font = `800 30px "Arial Narrow", Arial, sans-serif`;
    ctx.fillText(`RETURNING · ${value} BULK`, 74, H / 2 + 1);
    plate.texture.needsUpdate = true;
  }

  /**
   * Flash-reduction policy mirror (Engine.flashReduced(): knob OR
   * prefers-reduced-motion). Under reduction every animate() strobe holds a
   * steady/soft state; no telegraph ever disappears (fairness rule, 18.4).
   */
  setFlashReduced(reduced: boolean): void {
    this.flashReduced = reduced;
  }

  /**
   * Camera-proximity haze fade for the reinforced bulkhead (LOCKED-DESIGN
   * 18.10 4B capture: "close-range glass haze — the bulkhead goes milky when
   * the camera is within ~2 units"). Distance to the glass PLANE (the pane
   * spans the chamber width) maps to a 0..1 multiplier applied in animate():
   * full treatment beyond ~2.6 units, thinned to a floor at point-blank so the
   * barrier keeps presence while the chamber stays legible. Render-only.
   */
  updateGlassProximity(cameraPosition: THREE.Vector3): void {
    if (this.glassFaceZ === null) return;
    const distance = Math.abs(cameraPosition.z - this.glassFaceZ);
    const t = Math.min(1, Math.max(0, (distance - 1.1) / 1.5));
    this.glassFade = 0.14 + 0.86 * t;
  }

  /**
   * Route-freshness sign chips (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood): a
   * small FRESH/REPEAT/STALE + Grade-delta chip riding a target's existing
   * accessibility sign chip — the committed route's receiver, plus the aimed
   * candidate's would-be freshness. Icon + word + signed delta (never
   * hue-only); candidates draw a dashed frame ("if routed here"). The Engine
   * feeds sim-derived states each frame; entries absent from `entries` hide.
   * Sprites carry NO identifier/label userData — invisible to the QA seams.
   */
  setFreshnessChips(entries: ReadonlyArray<{ endpoint: "A" | "B"; name: string; state: "fresh" | "repeat" | "stale"; gradeDelta: number; candidate: boolean }>): void {
    for (const chip of this.freshnessChips.values()) chip.enabled = false;
    for (const entry of entries) {
      const key = `${entry.endpoint}:${entry.name}`;
      let chip = this.freshnessChips.get(key);
      if (!chip) {
        const target = [...this.authored.values()].find((candidate) => candidate.endpoint === entry.endpoint && candidate.name === entry.name);
        if (!target) continue;
        const canvas = document.createElement("canvas");
        canvas.width = 320; canvas.height = 72;
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false }));
        sprite.name = `arena-freshness-${entry.endpoint}-${entry.name}`;
        sprite.renderOrder = 31;
        sprite.scale.set(1.85, 0.42, 1);
        sprite.visible = false;
        this.group.add(sprite);
        chip = { sprite, canvas, texture, signature: "", target, enabled: false };
        this.freshnessChips.set(key, chip);
      }
      chip.enabled = true;
      const signature = `${entry.state}/${entry.gradeDelta}/${entry.candidate}`;
      if (signature !== chip.signature) {
        chip.signature = signature;
        drawFreshnessChip(chip.canvas, entry.state, entry.gradeDelta, entry.candidate);
        chip.texture.needsUpdate = true;
      }
    }
    for (const chip of this.freshnessChips.values()) {
      if (!chip.enabled) chip.sprite.visible = false;
    }
  }

  updateLabelVisibility(camera: THREE.Camera): void {
    for (const [object, target] of this.authored) {
      const label = target.label;
      // strip the previous frame's declutter stagger before re-projecting
      const priorOffset = (label.userData.declutterY as number | undefined) ?? 0;
      if (priorOffset !== 0) label.position.y -= priorOffset;
      label.userData.declutterY = 0;
      this.labelProbe.copy(target.pos).project(camera);
      const active = object.userData.active === true;
      label.visible = active && Math.abs(this.labelProbe.x) < .94 && this.labelProbe.y > -.9 && this.labelProbe.y < .96;
    }
    this.declutterLabels(camera);
    // freshness chips ride their target's sign chip: same visibility, docked
    // just above the (possibly decluttered) label
    for (const chip of this.freshnessChips.values()) {
      const label = chip.target.label;
      chip.sprite.visible = chip.enabled && label.visible;
      if (!chip.sprite.visible) continue;
      chip.sprite.position.set(
        label.position.x,
        label.position.y + label.scale.y / 2 + chip.sprite.scale.y / 2 + 0.07,
        label.position.z,
      );
    }
  }

  /**
   * Screen-space sign declutter (LOCKED-DESIGN 18.10 4B capture: SAFE·FLOOR
   * and TECH·WALL receiver chips stack at common chamber-view angles). When
   * two visible sign chips would overlap on screen, the higher one staggers
   * upward (world +y — screen-up in both canonical views) just enough to
   * clear, capped so a chip can never fling off its target. Overlap-driven:
   * at angles where nothing overlaps, nothing moves, so the QA framings and
   * the readability rules see today's exact layout. Two stability rules keep
   * a drifting camera from jittering the tags (Phase 4C): the keep-its-spot
   * winner is HYSTERETIC — while two chips' authored spans still overlap
   * vertically, last frame's order wins, so a near-tie in y can never swap
   * which chip climbs frame to frame — and the clearance pad ramps in with
   * overlap depth, so the lift is continuous at overlap onset instead of
   * popping by the full pad.
   */
  private declutterLabels(camera: THREE.Camera): void {
    const visible = this.declutterVisible;
    visible.length = 0;
    for (const target of this.authored.values()) {
      if (!target.label.visible) continue;
      let entry = this.declutterEntries.get(target.label);
      if (!entry) {
        entry = { label: target.label, rect: { x: 0, y: 0, width: 0, height: 0 } };
        this.declutterEntries.set(target.label, entry);
      }
      this.screenRectInto(target.label, camera, 1, 1, entry.rect);
      visible.push(entry);
    }
    if (visible.length < 2) return;
    // bottom-most first: it keeps its authored spot, later (higher) chips climb
    visible.sort((a, b) => {
      const dy = b.rect.y - a.rect.y;
      // clearly separated: authored screen order decides, ranks re-learn below
      if (Math.abs(dy) >= (a.rect.height + b.rect.height) / 2) return dy;
      // overlapping band: the previous frame's order is sticky for the whole
      // episode — the winner keeps its spot until the pair truly separates
      const rankA = (a.label.userData.declutterRank as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      const rankB = (b.label.userData.declutterRank as number | undefined) ?? Number.MAX_SAFE_INTEGER;
      return rankA === rankB ? dy : rankA - rankB;
    });
    for (let index = 0; index < visible.length; index++) visible[index]!.label.userData.declutterRank = index;
    const MAX_STAGGER_WORLD = 1.5;
    const PAD = 0.012; // screen-fraction breathing room between cleared chips
    for (let index = 1; index < visible.length; index++) {
      const item = visible[index]!;
      for (let prior = 0; prior < index; prior++) {
        const other = visible[prior]!;
        const overlapX = Math.min(item.rect.x + item.rect.width / 2, other.rect.x + other.rect.width / 2)
          - Math.max(item.rect.x - item.rect.width / 2, other.rect.x - other.rect.width / 2);
        const overlapY = Math.min(item.rect.y + item.rect.height / 2, other.rect.y + other.rect.height / 2)
          - Math.max(item.rect.y - item.rect.height / 2, other.rect.y - other.rect.height / 2);
        if (overlapX <= 0 || overlapY <= 0) continue;
        // convert the needed screen-fraction lift into world units via the
        // sprite's own projection (world height / projected height); the pad
        // fades in over its own depth so lift(overlap→0) → 0 (no onset pop)
        const worldPerFrac = item.label.scale.y / Math.max(item.rect.height, 1e-4);
        const clearance = overlapY + PAD * Math.min(1, overlapY / PAD);
        const lift = Math.min(MAX_STAGGER_WORLD, clearance * worldPerFrac);
        item.label.position.y += lift;
        item.label.userData.declutterY = ((item.label.userData.declutterY as number) ?? 0) + lift;
        // record the movement ACTUALLY applied (clamp-aware), so later overlap
        // tests in this pass measure the sprite where it really is
        item.rect.y -= lift / worldPerFrac;
      }
    }
  }

  updateOccupied(bluePosition: THREE.Vector3, orangePosition: THREE.Vector3): void {
    for (const [object, target] of this.authored) {
      const occupied = target.pos.distanceTo(target.endpoint === "A" ? bluePosition : orangePosition) < .45;
      object.userData.occupied = occupied;
      this.applyTargetVisualState(object, target, occupied);
    }
  }

  updateGameplay(tick: number, phase: PhaseSpec): void {
    for (const [object, target] of this.authored) {
      const active = target.endpoint === "A" ? phase.activeAnchors.includes(target.name as ArenaAnchor) : phase.activeReceivers.includes(target.name as ReceiverTier);
      object.userData.active = active;
      this.applyTargetVisualState(object, target, object.userData.occupied === true);
      (target.label.material as THREE.SpriteMaterial).opacity = active ? 1 : .22;
    }
    if (this.railObject) {
      const target = this.authored.get(this.railObject)!;
      const piece = this.definition.pieces.find((candidate) => candidate.id === target.pieceId)!;
      const motion = piece.motion;
      if (motion?.axis === "x") {
        // `railRate` scales the authored clock ONLY (see setRailRate) — amplitude
        // is untouched, so the panel still travels exactly the authored 24 m and
        // stays on the chamber's glass frontage. Ranked runs never set it.
        const nextX = motion.origin + Math.sin((tick * this.railRate) / motion.tickDivisor) * motion.amplitude;
        const deltaX = nextX - target.pos.x;
        target.pos.x = nextX;
        for (const visual of target.visuals) visual.position.x += deltaX;
        target.label.position.x += deltaX;
      }
    }
    // Lane shutters: sim-owned timing (phase.closedLane); the sweep/warning
    // choreography is applied in animate(). Objects without fx bookkeeping
    // (editor previews built before pieces exist) keep the legacy pop.
    for (const side of ["left", "right"] as const) {
      const object = this.shutters[side];
      if (!object) continue;
      const fx = this.laneShutterFx.get(side);
      if (fx) fx.targetClosed = phase.closedLane === side;
      else object.visible = phase.closedLane === side;
    }
  }

  /**
   * Sim-owned hazard-baffle pose (LOCKED-DESIGN 3.2): the engine feeds
   * Simulation.baffleYawAt evaluated at the interpolated render tick, so the
   * rendered bar is the deterministic kernel bar, not a decorative spin.
   * Without a simulation (arena editor preview) the bar simply rests at its
   * authored origin yaw — the editor authors the cycle parameters, the kernel
   * owns the clock.
   */
  setBafflePose(yaw: number): void {
    if (this.baffle) this.baffle.rotation.y = yaw;
  }

  /**
   * Simple-only sweep rate for the rail panel (CHASE IT!, LOCKED-DESIGN 18.25).
   *
   * WHY A RUNTIME SCALE AND NOT AN EDIT TO `arena.v1.json`. The authored motion
   * block is shared: `rail` is already in the RANKED anchor pool for phases 4-6
   * (seeds.ts ANCHORS), so retuning amplitude or tickDivisor in the definition
   * would silently retune the three official seeds' feel — which the "Simple
   * never touches ranked" boundary forbids. It would also collide with the
   * locked wording in Section 3.1, which names this anchor the *"Slow* seeded
   * rail-mounted panel". So the authored data keeps the locked slow sweep and
   * Simple opts into a faster one at runtime, in the presentation layer.
   *
   * Scaling the CLOCK rather than the amplitude is deliberate: peak speed is
   * `amplitude / tickDivisor * 60`, so rate 2 doubles the speed (1.71 -> 3.43
   * m/s) and halves the period (44.0 -> 22.0 s) while the ring still sweeps the
   * same authored x -12..+12. Widening instead would push the panel off the
   * 24 m-wide glass bulkhead it visually rides, and past amplitude ~18 it breaks
   * the telegraph bound against `precision` too.
   *
   * 3.43 m/s is ~40 % of the player's 8.5 m/s top speed: catchable at a walk,
   * but it no longer waits for you. THIS IS THE TUNING DIAL for the next family
   * dogfood — if the throw lead proves too hard for a five-year-old, lower it
   * here; nothing else in the slice depends on the value.
   */
  setRailRate(rate: number): void {
    this.railRate = rate > 0 ? rate : 1;
  }

  railPose(): { pos: THREE.Vector3; normal: THREE.Vector3 } | null {
    if (!this.railObject) return null;
    const target = this.authored.get(this.railObject)!;
    return { pos: target.pos, normal: target.normal };
  }

  private buildPiece(piece: ArenaPieceDefinition): void {
    if (isChutePiece(piece)) this.buildChute(piece);
    else if (piece.kind === "box") this.buildBox(piece);
    else if (piece.kind === "plane") this.buildPlane(piece);
    else if (piece.kind === "light") this.buildLight(piece);
    else if (piece.kind === "portal-anchor") this.buildPanel(piece);
    else this.buildReceiver(piece);
  }

  private buildChute(piece: ArenaPieceDefinition): void {
    const size = piece.size as Vec3;
    const group = new THREE.Group();
    group.name = `physical-drop-${piece.id}`;
    this.applyTransform(group, piece);
    // clean-industrial infeed machinery (18.1): painted/brushed metal housing,
    // dark throat, teal (conveyance) status strips
    const shell = new THREE.MeshStandardMaterial({ color: 0x596069, roughness: .5, metalness: .56 });
    const interior = new THREE.MeshStandardMaterial({ color: 0x14181c, emissive: 0x0c2830, emissiveIntensity: .22, roughness: .95, transparent: true, opacity: .12, depthWrite: false });
    const signal = new THREE.MeshStandardMaterial({ color: 0x7f949c, emissive: 0x2a9b9e, emissiveIntensity: .4, roughness: .34, metalness: .58 });
    const { sideWidth, topHeight, backDepth } = CHUTE_SHELL;
    const parts: Array<{ size: Vec3; position: Vec3; material: THREE.Material }> = [
      { size: [sideWidth, size[1], size[2]], position: [-size[0] / 2 + sideWidth / 2, 0, 0], material: shell },
      { size: [sideWidth, size[1], size[2]], position: [size[0] / 2 - sideWidth / 2, 0, 0], material: shell },
      { size: [size[0], topHeight, .5], position: [0, size[1] / 2 - topHeight / 2, 0], material: shell },
      { size: [size[0] - sideWidth * 2, .44, backDepth], position: [0, size[1] / 2 - topHeight - .22, size[2] / 2 - backDepth / 2], material: interior },
      { size: [size[0] - .62, .12, .1], position: [0, -size[1] / 2 + .14, -size[2] / 2 - .04], material: signal },
      { size: [.07, 1.45, .07], position: [-size[0] / 2 + .3, -.3, -size[2] / 2 - .36], material: signal },
      { size: [.07, 1.45, .07], position: [size[0] / 2 - .3, -.3, -size[2] / 2 - .36], material: signal },
      { size: [size[0] - .62, .07, .07], position: [0, .43, -size[2] / 2 - .36], material: signal },
    ];
    for (const [index, part] of parts.entries()) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(...part.size), part.material);
      mesh.name = index === 3 ? `chute-window-${piece.id}` : index >= 5 ? `chute-signal-${piece.id}-${index - 4}` : `chute-shell-${piece.id}-${index + 1}`;
      mesh.position.fromArray(part.position); mesh.castShadow = part.material !== interior; mesh.receiveShadow = true; group.add(mesh);
    }
    const channel = piece.id.endsWith("left") ? "01" : piece.id.endsWith("center") ? "02" : "03";
    const status = this.label(`INFEED ${channel}`, PALETTE.conveyance, `infeed-${channel}`, [1.25, .25], "infeed");
    status.position.set(0, -.02, -size[2] / 2 - .42); group.add(status);
    // Critical-overflow shutter plate (Section 7 "shutters all inbound
    // chutes" must be VISIBLE). Slides over the mouth; visual only — the
    // simulation already owns the spawn stop.
    const shutterMat = new THREE.MeshStandardMaterial({ color: 0x2a2c31, emissive: FEEDBACK_COLORS.alarm, emissiveIntensity: 0, roughness: .5, metalness: .6 });
    const shutterPlate = new THREE.Mesh(new THREE.BoxGeometry(size[0] - .58, 1.35, .12), shutterMat);
    shutterPlate.name = `chute-overflow-shutter-${piece.id}`;
    const openY = size[1] / 2 - .5;
    const closedY = -size[1] / 2 + .62;
    shutterPlate.position.set(0, openY, -size[2] / 2 - .1);
    shutterPlate.visible = false;
    group.add(shutterPlate);
    const lane: ChuteId = piece.id.endsWith("left") ? "left" : piece.id.endsWith("center") ? "center" : "right";
    this.chuteFx.set(lane, { signal, baseEmissive: .4, shutter: shutterPlate, shutterMat, openY, closedY });
    this.addObject(piece, group);
  }

  private applyTargetVisualState(object: THREE.Object3D, target: AuthoredTarget, occupied: boolean): void {
    const active = object.userData.active === true;
    const updated = new Set<THREE.Material>();
    for (const visual of target.visuals) {
      const material = (visual as THREE.Mesh).material as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial | undefined;
      if (!material || updated.has(material)) continue;
      updated.add(material);
      material.transparent = true;
      const baseOpacity = active ? (visual.userData.activeOpacity ?? 1) : (visual.userData.inactiveOpacity ?? .14);
      material.opacity = baseOpacity * (occupied ? .12 : 1);
      if ("emissiveIntensity" in material) {
        const baseEmissive = active ? (visual.userData.activeEmissive ?? (target.endpoint === "A" ? .18 : .35)) : (visual.userData.inactiveEmissive ?? .015);
        // reward-ladder receiver flash (LOCKED-DESIGN 18.4): payoff pulse layers on top
        const pulse = target.endpoint === "B" && active ? this.receiverPulseLevels[target.name as ReceiverTier] : 0;
        material.emissiveIntensity = baseEmissive * (occupied ? .08 : 1) + pulse * 1.5;
      }
    }
  }

  private buildBox(piece: ArenaPieceDefinition): void {
    if (piece.id === "recovery-handle") {
      this.buildReleaseHandle(piece);
      return;
    }
    const size = piece.size as Vec3;
    const material = this.boxMaterial(piece.preset);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
    this.applyTransform(mesh, piece);
    mesh.castShadow = material.opacity === 1;
    mesh.receiveShadow = true;
    this.addObject(piece, mesh);
    if (piece.id === "hazard-baffle") {
      this.baffle = mesh;
      // rotating-gate dressing (18.3): a pivot hub and swept warning tips ride
      // the bar as children, so the sim-driven yaw (setBafflePose) carries them.
      // The bar mesh itself keeps the EXACT authored box, so the deterministic
      // gate pose the kernel owns is never misrepresented.
      const hub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3, 0.36, size[1] + 0.34, 12),
        new THREE.MeshStandardMaterial(HERO_THEME.receiver.guardDark),
      );
      hub.name = "baffle-hub";
      const tipMat = new THREE.MeshStandardMaterial({ color: PALETTE.tierHazard, emissive: PALETTE.tierHazard, emissiveIntensity: 0.5, roughness: 0.4, metalness: 0.3 });
      const tips = [-1, 1].map((sign) => {
        const tip = new THREE.Mesh(new THREE.BoxGeometry(0.22, size[1] + 0.14, size[2] + 0.14), tipMat);
        tip.name = "baffle-tip";
        tip.position.x = sign * (size[0] / 2 - 0.11);
        return tip;
      });
      for (const child of [hub, ...tips]) {
        child.userData.arenaPieceId = piece.id;
        child.castShadow = true;
        mesh.add(child);
      }
    }
    // presentation handles: conveyor-reversal warning scroll + glass stress flicker
    if (piece.preset === "conveyor" && "emissiveIntensity" in material && material.map) {
      this.conveyorFx.push({ material, texture: material.map, baseEmissive: material.emissiveIntensity });
    }
    if (piece.preset === "glass") {
      this.glassFx.push({ material, baseOpacity: material.opacity });
    }
    if (piece.id === "return-pad" && "emissiveIntensity" in material) {
      // Return-pad ring fill (LOCKED-DESIGN 18.12 §3.4): the pad brightens with the
      // launch spin-up charge (setReturnPadCharge). Base emissive is its rest glow.
      this.returnPadMat = material as THREE.MeshStandardMaterial;
      this.returnPadBaseEmissive = this.returnPadMat.emissiveIntensity;
    }
    if (piece.label) this.addLooseLabel(piece, piece.label);
  }

  /**
   * Maw FEED GUARD (LOCKED-DESIGN 18.12): the release-handle hero asset re-dressed
   * as a fixed guard post at the intake lip (repositioned + re-stenciled "FEED
   * POINT" in arena.v1.json). No pivot/pull rig anymore — the m1.3.0 recovery is
   * hand-feeding, not a lever. Its lamp glow rides setJamActive so the feed point
   * lights teal while a jam waits. Visual-only: the piece is non-collidable.
   */
  private buildReleaseHandle(piece: ArenaPieceDefinition): void {
    const size = piece.size as Vec3;
    const root = new THREE.Group();
    this.applyTransform(root, piece);
    this.addObject(piece, root);
    const params = { height: size[1] ?? 1.7 };
    const bind = (asset: HeroAsset): void => {
      this.mountHero(asset, root, piece.id);
      this.recoveryHandleMat = resolveHeroGlowMaterial(
        asset,
        "lamp",
        "handle-lamp-lens",
      );
      this.recoveryHandleBaseEmissive = this.recoveryHandleMat?.emissiveIntensity ?? 0;
    };
    bind(createHeroAsset("release-handle", params));
    upgradeHeroAsset("release-handle", (asset) => {
      if (this.disposed) {
        asset.dispose();
        return;
      }
      this.heroMounts.get("release-handle")?.dispose();
      bind(asset);
    });
  }

  /**
   * Mount a hero asset (LOCKED-DESIGN 18.3 GLTF-ready seam): parents the root,
   * tags every mesh with the owning `arenaPieceId` (the QA occlusion walk keys
   * on it), applies the active quality tier, and remembers the asset so a
   * later authored-model swap can dispose it. Returns the asset's meshes.
   */
  private mountHero(asset: HeroAsset, parent: THREE.Object3D, pieceId: string): THREE.Mesh[] {
    parent.add(asset.root);
    this.heroMounts.set(asset.id, asset);
    applyHeroQuality(asset.root, this.qualityLevel);
    const meshes: THREE.Mesh[] = [];
    asset.root.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.userData.arenaPieceId = pieceId;
      meshes.push(mesh);
    });
    return meshes;
  }

  /**
   * Furnace maw (LOCKED-DESIGN 18.3/2.1): the chamber's focal point, built
   * from the hero seam — segmented teeth frame, glowing throat with a
   * quality-tiered heat-shimmer treatment, intake rollers and grate — so
   * incineration reads as entry INTO the maw (dogfood #1). The group keeps the
   * authored piece transform; animate() drives the throat flicker + shimmer.
   */
  private buildPlane(piece: ArenaPieceDefinition): void {
    const size = piece.size as [number, number];
    const group = new THREE.Group();
    this.applyTransform(group, piece);
    this.addObject(piece, group);
    const params = { width: size[0], height: size[1] };
    const bind = (asset: HeroAsset): void => {
      seatFurnaceThroat(asset);
      const shimmer = resolveFurnaceShimmer(asset);
      this.mountHero(asset, group, piece.id);
      const fire = asset.root.getObjectByName("furnace-throat-fire") as THREE.Mesh | null;
      this.fireMat = fire && fire.material instanceof THREE.MeshStandardMaterial ? fire.material : null;
      this.furnaceShimmer = shimmer;
    };
    bind(createHeroAsset("furnace-maw", params));
    upgradeHeroAsset("furnace-maw", (asset) => {
      if (this.disposed) {
        asset.dispose();
        return;
      }
      this.heroMounts.get("furnace-maw")?.dispose();
      bind(asset);
    });
  }

  private buildLight(piece: ArenaPieceDefinition): void {
    const values = piece.size as Vec3;
    const light = new THREE.PointLight(0xff7a2a, values[0], values[1], values[2]);
    light.position.fromArray(piece.position); light.visible = piece.visible; this.addObject(piece, light); this.fireLight = light;
  }

  private buildPanel(piece: ArenaPieceDefinition): void {
    const size = piece.size as [number, number];
    const normal = new THREE.Vector3().fromArray(piece.normal!).normalize();
    const orientation = portalSurfaceOrientation(normal);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(size[0], size[1], .14), new THREE.MeshStandardMaterial({ color: ARENA_ACCENTS.anchorPanel.color, emissive: ARENA_ACCENTS.anchorPanel.emissive, emissiveIntensity: ARENA_ACCENTS.anchorPanel.emissiveIntensity, roughness: ARENA_ACCENTS.anchorPanel.roughness, metalness: ARENA_ACCENTS.anchorPanel.metalness, transparent: true }));
    mesh.name = `portal-target-A-${piece.role}`;
    mesh.position.fromArray(piece.position); mesh.quaternion.copy(orientation); mesh.userData.activeEmissive = .1;
    const frameMaterial = new THREE.MeshStandardMaterial({ color: ARENA_ACCENTS.anchorSteel, emissive: 0x28718a, emissiveIntensity: .18, roughness: .4, metalness: .62, transparent: true });
    const center = mesh.position.clone().addScaledVector(normal, .1);
    const border = .11; const inset = .06;
    const frame = [
      this.orientedMesh(new THREE.BoxGeometry(size[0] + inset * 2, border, .055), frameMaterial, center, orientation, [0, size[1] / 2 + inset, 0]),
      this.orientedMesh(new THREE.BoxGeometry(size[0] + inset * 2, border, .055), frameMaterial, center, orientation, [0, -size[1] / 2 - inset, 0]),
      this.orientedMesh(new THREE.BoxGeometry(border, size[1], .055), frameMaterial, center, orientation, [-size[0] / 2 - inset, 0, 0]),
      this.orientedMesh(new THREE.BoxGeometry(border, size[1], .055), frameMaterial, center, orientation, [size[0] / 2 + inset, 0, 0]),
    ];
    const registration = this.orientedMesh(new THREE.BoxGeometry(Math.min(size[0] * .32, 1.15), .065, .065), frameMaterial, center, orientation, [0, 0, .02]);
    for (const visual of [...frame, registration]) { visual.userData.activeEmissive = .28; visual.userData.inactiveOpacity = .12; }
    const panelDetails: THREE.Object3D[] = [];
    if (piece.role === "precision") {
      // Occlusion-by-design recesses the tower ring behind the lip (D4), so the
      // aperture is invisible from the follow camera AND the throw vantage — a kid
      // ends up throwing at a bare glow (Phase-4F finding). Draw an X-RAY aperture
      // ring at the ring plane that renders THROUGH the lip/roof (depthTest off),
      // marking exactly where to throw. Visual-only: the physical ground-throw
      // defeat (the collidable lip) is untouched, so D4 still holds. In `visuals`
      // so applyTargetVisualState glows it only while precision is the live anchor.
      const minSize = Math.min(size[0], size[1]);
      const apertureMat = new THREE.MeshBasicMaterial({ color: ARENA_ACCENTS.anchorPanel.emissive, transparent: true, depthTest: false, depthWrite: false });
      const aperture = this.orientedMesh(new THREE.RingGeometry(minSize * .5, minSize * .68, 40), apertureMat, center, orientation, [0, 0, .06]);
      aperture.name = "precision-aperture";
      aperture.renderOrder = 27;
      aperture.userData.activeOpacity = .92;
      aperture.userData.inactiveOpacity = .1;
      panelDetails.push(aperture);
    }
    if (piece.role === "ramp") {
      const supportCenter = mesh.position.clone().addScaledVector(normal, RAMP_SUPPORT.normalOffset);
      const support = this.orientedMesh(new THREE.BoxGeometry(size[0] + RAMP_SUPPORT.margin, size[1] + RAMP_SUPPORT.margin, RAMP_SUPPORT.depth), new THREE.MeshStandardMaterial({ color: 0x27313b, roughness: .66, metalness: .46 }), supportCenter, orientation);
      support.userData.arenaPieceId = piece.id;
      support.userData.affordance = "support";
      support.castShadow = support.receiveShadow = true; this.group.add(support);
      const tangent = new THREE.Vector3(1, 0, 0).applyQuaternion(orientation);
      const incline = new THREE.Vector3(0, 1, 0).applyQuaternion(orientation);
      const cheekMaterial = new THREE.MeshStandardMaterial({ color: 0x41616e, emissive: 0x1a5361, emissiveIntensity: .2, roughness: .62, metalness: .46, side: THREE.DoubleSide });
      for (const [side, name] of [[-1, "ramp-cheek-left"], [1, "ramp-cheek-right"]] as const) {
        const edge = mesh.position.clone().addScaledVector(tangent, side * (size[0] / 2 + .08));
        const low = edge.clone().addScaledVector(incline, -size[1] / 2);
        const high = edge.clone().addScaledVector(incline, size[1] / 2);
        const floorLow = low.clone(); floorLow.y = .02;
        const floorHigh = high.clone(); floorHigh.y = .02;
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute([
          low.x, low.y, low.z, high.x, high.y, high.z, floorHigh.x, floorHigh.y, floorHigh.z,
          low.x, low.y, low.z, floorHigh.x, floorHigh.y, floorHigh.z, floorLow.x, floorLow.y, floorLow.z,
        ], 3));
        geometry.computeVertexNormals();
        const cheek = new THREE.Mesh(geometry, cheekMaterial); cheek.name = name; cheek.castShadow = cheek.receiveShadow = true; cheek.userData.arenaPieceId = piece.id;
        // Semantic affordance markers (QA keys on these, never on names/counts).
        cheek.userData.affordance = "support";
        this.group.add(cheek);
      }
      const treadMaterial = new THREE.MeshStandardMaterial({ color: 0x9bb8c4, emissive: 0x24596a, emissiveIntensity: .25, roughness: .42, metalness: .58, transparent: true });
      for (const [index, y] of [-.95, -.35, .25, .85].entries()) {
        const tread = this.orientedMesh(new THREE.BoxGeometry(size[0] * .62, .075, .055), treadMaterial, center, orientation, [0, y, .035]);
        tread.name = `ramp-tread-${index + 1}`; tread.userData.arenaPieceId = piece.id; tread.userData.affordance = "traction"; tread.userData.activeEmissive = .35; tread.userData.inactiveOpacity = .16;
        panelDetails.push(tread); this.group.add(tread);
      }
    }
    const label = this.targetLabel(piece);
    const visuals: THREE.Object3D[] = [mesh, ...frame, registration, ...panelDetails];
    this.group.add(...visuals, label); this.aimTargets.push(mesh); this.addObject(piece, mesh, false);
    this.authored.set(mesh, { endpoint: "A", name: piece.role as ArenaAnchor, pos: mesh.position.clone(), normal, visuals, label, pieceId: piece.id });
  }

  private buildReceiver(piece: ArenaPieceDefinition): void {
    const normal = new THREE.Vector3().fromArray(piece.normal!).normalize();
    const role = piece.role as ReceiverTier;
    const color = RECEIVER_TIER_COLORS[role];
    const radius = Math.min(...piece.size) / 2;
    const orientation = portalSurfaceOrientation(normal);
    const baseIntensity = role === "safe" ? .18 : role === "technical" ? .3 : .42;
    const apertureGeometry = role === "technical" ? new THREE.PlaneGeometry(piece.size[0], piece.size[1]) : new THREE.CircleGeometry(radius, role === "hazard" ? 8 : 40);
    const mesh = new THREE.Mesh(apertureGeometry, new THREE.MeshStandardMaterial({ color: role === "safe" ? 0x15271f : role === "technical" ? 0x2b271b : 0x2b1820, emissive: color, emissiveIntensity: baseIntensity, roughness: .48, metalness: .52, transparent: true, side: THREE.DoubleSide }));
    mesh.name = `portal-target-B-${piece.role}`;
    mesh.position.fromArray(piece.position).addScaledVector(normal, .12); mesh.quaternion.copy(orientation); mesh.userData.activeEmissive = baseIntensity;
    const center = new THREE.Vector3().fromArray(piece.position).addScaledVector(normal, .16);
    const visuals: THREE.Object3D[] = [mesh];
    // Hero machinery (LOCKED-DESIGN 18.3/3.2): tier RISK encoded in mechanical
    // form — safe's broad guarded funnel, technical's shrouded precision slot,
    // hazard's caged overhead assembly — replacing the flat locator strokes.
    // The aperture mesh above stays the untouched aim/raycast/QA target.
    visuals.push(...this.mountReceiverHero(role, piece, center, orientation));
    if (role === "hazard") {
      // Baffle gate lamp (LOCKED-DESIGN 3.2 explicit timing feedback, 18.4
      // telegraphs): in-world open/blocked cue consistent with the HUD gate
      // readout. Deliberately NOT in `visuals` — the lamp reports gate state,
      // not target active/occupied state; animate() owns its color.
      const lampMat = new THREE.MeshStandardMaterial({ color: 0x39414d, emissive: 0x39414d, emissiveIntensity: .05, roughness: .3, metalness: .4 });
      const lamp = this.orientedMesh(new THREE.SphereGeometry(.17, 14, 10), lampMat, center, orientation, [-(radius + .55), 0, .1]);
      lamp.name = "hazard-gate-lamp";
      lamp.userData.arenaPieceId = piece.id;
      this.group.add(lamp);
      this.baffleLampMat = lampMat;
    }
    const beacon = this.orientedMesh(new THREE.SphereGeometry(.13, 14, 10), new THREE.MeshStandardMaterial({ color: PALETTE.receiverOrange, emissive: PALETTE.receiverOrange, emissiveIntensity: .85, roughness: .25 }), center, orientation, [0, radius + .5, .08]);
    visuals.push(beacon);
    // hero meshes carry their authored glow in userData already; only fill the
    // gaps so applyTargetVisualState can drive active/occupied state uniformly
    for (const visual of visuals) {
      if (visual.userData.activeEmissive === undefined) visual.userData.activeEmissive = visual === beacon ? .85 : baseIntensity;
      if (visual.userData.inactiveOpacity === undefined) visual.userData.inactiveOpacity = .1;
    }
    const label = this.targetLabel(piece);
    this.group.add(...visuals.filter((visual) => visual.parent === null), label); this.aimTargets.push(mesh); this.addObject(piece, mesh, false);
    this.authored.set(mesh, { endpoint: "B", name: role, pos: new THREE.Vector3().fromArray(piece.position), normal, visuals, label, pieceId: piece.id });
  }

  /**
   * Receiver hero machinery through the GLTF-ready seam (18.3): procedural
   * kit-bash today; an authored public/models/receiver-<tier>.glb hot-swaps in
   * with zero rework here. Meshes join the target's `visuals` so the existing
   * active/inactive/occupied state driver and the silhouette QA measure them.
   */
  private mountReceiverHero(role: ReceiverTier, piece: ArenaPieceDefinition, center: THREE.Vector3, orientation: THREE.Quaternion): THREE.Mesh[] {
    const id: HeroAssetId = `receiver-${role}`;
    const params = { width: piece.size[0], height: piece.size[1] };
    const place = (asset: HeroAsset): THREE.Mesh[] => {
      asset.root.position.copy(center);
      asset.root.quaternion.copy(orientation);
      const meshes = this.mountHero(asset, this.group, piece.id);
      for (const heroMesh of meshes) {
        const material = heroMesh.material as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;
        if (heroMesh.userData.activeEmissive === undefined) {
          heroMesh.userData.activeEmissive = "emissiveIntensity" in material ? material.emissiveIntensity : 0;
        }
        if (heroMesh.userData.inactiveOpacity === undefined) heroMesh.userData.inactiveOpacity = .1;
      }
      return meshes;
    };
    upgradeHeroAsset(id, (asset) => {
      if (this.disposed) {
        asset.dispose();
        return;
      }
      const target = [...this.authored.values()].find((entry) => entry.endpoint === "B" && entry.name === role);
      const previous = this.heroMounts.get(id);
      if (!target) {
        asset.dispose();
        return;
      }
      if (previous) {
        const stale = new Set<THREE.Object3D>();
        previous.root.traverse((node) => stale.add(node));
        target.visuals = target.visuals.filter((visual) => !stale.has(visual));
        previous.dispose();
      }
      target.visuals.push(...place(asset));
    });
    return place(createHeroAsset(id, params));
  }

  private orientedMesh(geometry: THREE.BufferGeometry, material: THREE.Material, center: THREE.Vector3, orientation: THREE.Quaternion, local: Vec3 = [0, 0, 0], localRotation: Vec3 = [0, 0, 0]): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(center).add(new THREE.Vector3().fromArray(local).applyQuaternion(orientation));
    mesh.quaternion.copy(orientation).multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...localRotation)));
    return mesh;
  }

  private targetLabel(piece: ArenaPieceDefinition): THREE.Sprite {
    const definition = piece.label!;
    const anchor = piece.kind === "portal-anchor";
    const color = anchor ? PALETTE.anchorBlue : RECEIVER_TIER_COLORS[piece.role as ReceiverTier];
    const glyph: SignGlyph = anchor ? "anchor" : (piece.role as ReceiverTier);
    const sprite = this.label(definition.text, color, String(piece.role), definition.scale, glyph);
    sprite.position.fromArray(piece.position).add(new THREE.Vector3().fromArray(definition.offset));
    return sprite;
  }

  private addLooseLabel(piece: ArenaPieceDefinition, definition: ArenaLabelDefinition): void {
    const purge = piece.id === "purge-station";
    const sprite = this.label(definition.text, PALETTE.tierHazard, purge ? "purge" : piece.id, definition.scale, purge ? "purge" : "anchor");
    sprite.position.fromArray(piece.position).add(new THREE.Vector3().fromArray(definition.offset));
    sprite.userData.arenaPieceId = piece.id; this.group.add(sprite);
  }

  private applyTransform(object: THREE.Object3D, piece: ArenaPieceDefinition): void {
    object.position.fromArray(piece.position); object.rotation.set(...piece.rotation); object.visible = piece.visible;
  }

  private addObject(piece: ArenaPieceDefinition, object: THREE.Object3D, add = true): void {
    object.name = object.name || `arena-piece-${piece.id}`;
    object.userData.arenaPieceId = piece.id;
    if (add) this.group.add(object);
    this.objects.set(piece.id, object);
  }

  private boxMaterial(preset: string): THREE.MeshStandardMaterial | THREE.MeshBasicMaterial {
    if (preset === "floor") {
      const textures = makeFloorTextures();
      return new THREE.MeshStandardMaterial({ ...ARENA_TEXTURED_PRESETS.floor, map: textures.map, emissiveMap: textures.emissive });
    }
    if (preset === "hazard-stripe") {
      const texture = makeHazardTexture();
      return new THREE.MeshStandardMaterial({ ...ARENA_TEXTURED_PRESETS["hazard-stripe"], map: texture, emissiveMap: texture });
    }
    if (preset === "conveyor") {
      const texture = makeConveyorTexture();
      return new THREE.MeshStandardMaterial({ ...ARENA_TEXTURED_PRESETS.conveyor, map: texture, emissiveMap: texture });
    }
    if (preset === "glass") {
      // reinforced-glass pane (18.1): cool laminated tint, low roughness + raised
      // envMapIntensity so the room reflects statically (no per-frame pass), so
      // the sealed barrier reads as glass, not a flat tint. The steel grid, edge
      // tint, and smudge/impact overlay are built by buildGlassBulkhead().
      const pane = new THREE.MeshStandardMaterial({
        color: GLASS.pane.color, transparent: true, opacity: GLASS.pane.opacity, depthWrite: false,
        roughness: GLASS.pane.roughness, metalness: GLASS.pane.metalness, envMapIntensity: GLASS.pane.envMapIntensity,
      });
      this.glassPaneMats.push(pane);
      return pane;
    }
    const value = ARENA_BOX_PRESETS[preset] ?? ARENA_BOX_FALLBACK;
    if (value.basic) return new THREE.MeshBasicMaterial({ color: value.color, transparent: (value.opacity ?? 1) < 1, opacity: value.opacity ?? 1, depthWrite: value.depthWrite ?? true });
    // painted-steel bolt/seam identity on the hero sorting island (18.1)
    const map = preset === "platform" ? makeSteelTexture() : undefined;
    if (map) map.repeat.set(2.4, 1);
    return new THREE.MeshStandardMaterial({
      color: value.color,
      map,
      roughness: value.roughness ?? .78,
      metalness: value.metalness ?? .12,
      transparent: (value.opacity ?? 1) < 1,
      opacity: value.opacity ?? 1,
      depthWrite: value.depthWrite ?? true,
      emissive: value.emissive ?? 0x000000,
      emissiveIntensity: value.emissiveIntensity ?? 0,
    });
  }

  /**
   * Slimmed accessibility/QA label chip (LOCKED-DESIGN 18.1). The route /
   * multiplier / infeed identity has MIGRATED onto the diegetic surface graphics
   * (painted floor brackets, stencilled multiplier plates, chute/enclosure
   * signage); this camera-facing chip is the "slimmed label layer" the lock
   * permits to survive as an accessibility + QA overlay — a compact FORM glyph +
   * code tag, not the old lightbox billboard (no status header, inner border, or
   * rivets, and a reduced footprint). The FORM glyph + text carry the identity so
   * the tier triad stays colorblind-safe (not hue-only), and `userData.label` /
   * `userData.identifier` remain the stable seam the QA/editor readability checks
   * key on. `glyph` selects the FORM; it defaults to the anchor reticle.
   */
  private label(text: string, color: number, semantic: string, scale: [number, number], glyph: SignGlyph = "anchor"): THREE.Sprite {
    const material = new THREE.SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false });
    const sprite = new THREE.Sprite(material);
    sprite.name = `arena-label-${semantic}`;
    sprite.userData.label = semantic;
    sprite.userData.identifier = true;
    // Repaint-source spec (LOCKED-DESIGN 18.11): stashed so setSignageMode can
    // swap between the standard code chip and the Simple icon-first variant.
    sprite.userData.labelSpec = { text, color, glyph, scale };
    sprite.renderOrder = 30;
    this.paintLabelSprite(sprite);
    return sprite;
  }

  /**
   * (Re)paint one identifier sign chip from its stashed spec, honoring the
   * current signage mode. Standard = the slimmed FORM-glyph + code chip (18.1).
   * Simple = an icon-first plate: an enlarged FORM glyph + color, text stripped
   * (the tier still reads by FORM, colorblind-safe), flagged userData.simpleSign.
   */
  private paintLabelSprite(sprite: THREE.Sprite): void {
    const spec = sprite.userData.labelSpec as { text: string; color: number; glyph: SignGlyph; scale: [number, number] };
    const accent = cssHex(spec.color);
    const material = sprite.material as THREE.SpriteMaterial;
    material.map?.dispose();
    if (this.simpleSignage) {
      const S = 192;
      const canvas = document.createElement("canvas"); canvas.width = S; canvas.height = S;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = SIGNAGE.plate.body; ctx.roundRect(6, 6, S - 12, S - 12, 22); ctx.fill();
      ctx.strokeStyle = accent; ctx.lineWidth = 6; ctx.roundRect(6, 6, S - 12, S - 12, 22); ctx.stroke();
      drawSignGlyph(ctx, spec.glyph, S / 2, S / 2, 58, accent);
      material.map = new THREE.CanvasTexture(canvas);
      // square, glyph-sized plate keyed off the chip height (kept compact so it
      // never leaves the frame at either QA resolution)
      const side = spec.scale[1] * 1.35;
      sprite.scale.set(side, side, 1);
      sprite.userData.simpleSign = true;
    } else {
      const W = 448; const H = 84; const SLIM = .82;
      const canvas = document.createElement("canvas"); canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d")!;
      // compact accessibility chip: recessed body + accent outline, the tier hue a
      // thin frame rather than the old lit lightbox slab (18.1 slimmed layer)
      ctx.fillStyle = SIGNAGE.plate.body; ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.fill();
      ctx.strokeStyle = accent; ctx.lineWidth = 2; ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.stroke();
      // FORM glyph — colorblind-safe redundancy for the identity
      drawSignGlyph(ctx, spec.glyph, 40, H / 2, 20, accent);
      // stencilled identifier, auto-fit into the remaining width
      const left = 74; const maxWidth = W - left - 14;
      ctx.fillStyle = SIGNAGE.plate.text; ctx.textAlign = "left"; ctx.textBaseline = "middle";
      let size = 32;
      for (; size > 14; size -= 2) { ctx.font = `800 ${size}px "Arial Narrow", Arial, sans-serif`; if (ctx.measureText(spec.text).width <= maxWidth) break; }
      ctx.fillText(spec.text, left, H / 2 + 1);
      material.map = new THREE.CanvasTexture(canvas);
      sprite.scale.set(spec.scale[0] * SLIM, spec.scale[1] * SLIM, 1);
      sprite.userData.simpleSign = false;
    }
    material.needsUpdate = true;
  }

  /**
   * Switch every identifier sign chip between the standard code chip and the
   * Simple icon-first variant (LOCKED-DESIGN 18.11). Idempotent; the Engine
   * calls it at run start from ctx.mode. Only repaints when the mode changes.
   */
  setSignageMode(simple: boolean): void {
    if (this.simpleSignage === simple) return;
    this.simpleSignage = simple;
    for (const object of this.labelObjects()) {
      if (object instanceof THREE.Sprite && object.userData.labelSpec) this.paintLabelSprite(object);
    }
  }
}

/** Role/tier FORM vocabulary for the diegetic signs (colorblind-safe, 18.3). */
type SignGlyph = "anchor" | "safe" | "technical" | "hazard" | "infeed" | "purge";

/**
 * Draws the safety-graphic FORM glyph for a sign/plate: a reticle for anchors,
 * a broad aperture for safe, a guarded slot for technical, warning chevrons for
 * hazard, a funnel for infeed, an eject triangle for purge. The FORM (not the
 * hue) carries the tier so the signage stays readable for colorblind players.
 */
function drawSignGlyph(ctx: CanvasRenderingContext2D, glyph: SignGlyph, cx: number, cy: number, r: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 2.4; ctx.lineJoin = "round"; ctx.lineCap = "round";
  if (glyph === "anchor") {
    for (const sx of [-1, 1] as const) for (const sy of [-1, 1] as const) {
      ctx.beginPath(); ctx.moveTo(cx + sx * r, cy + sy * r - sy * r * .5); ctx.lineTo(cx + sx * r, cy + sy * r); ctx.lineTo(cx + sx * r - sx * r * .5, cy + sy * r); ctx.stroke();
    }
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fill();
  } else if (glyph === "safe") {
    ctx.lineWidth = 3.4; ctx.beginPath(); ctx.arc(cx, cy, r * .82, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, r * .32, 0, Math.PI * 2); ctx.fill();
  } else if (glyph === "technical") {
    ctx.fillRect(cx - r * .16, cy - r * .8, r * .32, r * 1.6); // narrow slot
    for (const sx of [-1, 1] as const) { // guard brackets
      ctx.beginPath(); ctx.moveTo(cx + sx * r * .7, cy - r * .8); ctx.lineTo(cx + sx * r * .5, cy - r * .8); ctx.lineTo(cx + sx * r * .5, cy + r * .8); ctx.lineTo(cx + sx * r * .7, cy + r * .8); ctx.stroke();
    }
  } else if (glyph === "hazard") {
    for (const oy of [-r * .55, 0, r * .55]) { // stacked warning chevrons
      ctx.beginPath(); ctx.moveTo(cx - r * .7, cy + oy - r * .28); ctx.lineTo(cx, cy + oy + r * .18); ctx.lineTo(cx + r * .7, cy + oy - r * .28); ctx.stroke();
    }
  } else if (glyph === "infeed") {
    ctx.beginPath(); ctx.moveTo(cx - r * .8, cy - r * .7); ctx.lineTo(cx + r * .8, cy - r * .7); ctx.lineTo(cx + r * .28, cy + r * .2); ctx.lineTo(cx - r * .28, cy + r * .2); ctx.closePath(); ctx.stroke(); // funnel
    ctx.beginPath(); ctx.moveTo(cx, cy + r * .1); ctx.lineTo(cx, cy + r * .8); ctx.moveTo(cx - r * .3, cy + r * .5); ctx.lineTo(cx, cy + r * .8); ctx.lineTo(cx + r * .3, cy + r * .5); ctx.stroke(); // down arrow
  } else { // purge — eject triangle with an alert bar
    ctx.beginPath(); ctx.moveTo(cx, cy - r * .8); ctx.lineTo(cx + r * .8, cy + r * .6); ctx.lineTo(cx - r * .8, cy + r * .6); ctx.closePath(); ctx.stroke();
    ctx.fillRect(cx - 1.6, cy - r * .25, 3.2, r * .55); ctx.beginPath(); ctx.arc(cx, cy + r * .42, 2.1, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
}

/**
 * Route-freshness chip face (LOCKED-DESIGN 8.3 / 18.10 fourth dogfood): a
 * compact plate with a direction-triangle FORM (up = fresh, right = repeat,
 * down = stale — never hue-only), the state word, and the signed Grade delta.
 * Colors walk the locked taxonomy (fresh = safe green, repeat = partial blue,
 * stale = the STALE amber). Candidates ("if routed here") draw dashed frames.
 */
function drawFreshnessChip(canvas: HTMLCanvasElement, state: "fresh" | "repeat" | "stale", gradeDelta: number, candidate: boolean): void {
  const ctx = canvas.getContext("2d")!;
  const W = canvas.width; const H = canvas.height;
  const accent = cssHex(state === "fresh" ? PALETTE.tierSafe : state === "repeat" ? FEEDBACK_COLORS.partial : FEEDBACK_COLORS.stale);
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = SIGNAGE.plate.body;
  ctx.beginPath(); ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.fill();
  ctx.strokeStyle = accent; ctx.lineWidth = 2.6;
  ctx.setLineDash(candidate ? [9, 6] : []);
  ctx.beginPath(); ctx.roundRect(2, 4, W - 4, H - 8, 10); ctx.stroke();
  ctx.setLineDash([]);
  // direction-triangle FORM glyph
  const cx = 34; const cy = H / 2; const r = 13;
  ctx.fillStyle = accent;
  ctx.beginPath();
  if (state === "fresh") { ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy + r * 0.8); ctx.lineTo(cx - r, cy + r * 0.8); }
  else if (state === "repeat") { ctx.moveTo(cx - r * 0.8, cy - r); ctx.lineTo(cx + r, cy); ctx.lineTo(cx - r * 0.8, cy + r); }
  else { ctx.moveTo(cx - r, cy - r * 0.8); ctx.lineTo(cx + r, cy - r * 0.8); ctx.lineTo(cx, cy + r); }
  ctx.closePath(); ctx.fill();
  const delta = gradeDelta < 0 ? `−${Math.abs(gradeDelta)}` : `+${gradeDelta}`;
  ctx.fillStyle = accent; ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.font = `800 34px "Arial Narrow", Arial, sans-serif`;
  ctx.fillText(`${state.toUpperCase()} ${delta}`, 60, H / 2 + 1);
}

/**
 * Painted stencil plate texture for on-surface floor markings: a worn off-white
 * FORM glyph + stencilled text on a transparent field, so a facility marking can
 * be laid flat on concrete (deadpan safety paint reinforcing the lightbox sign).
 */
function makeStencilPlate(text: string, glyph: SignGlyph): THREE.CanvasTexture {
  const W = 512; const H = 256;
  const canvas = document.createElement("canvas"); canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const paint = cssHex(SIGNAGE.paint.stencil);
  ctx.strokeStyle = paint; ctx.lineWidth = 6; ctx.globalAlpha = .85;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  drawSignGlyph(ctx, glyph, 78, H / 2, 42, paint);
  ctx.globalAlpha = 1; ctx.fillStyle = paint; ctx.textAlign = "left"; ctx.textBaseline = "middle";
  let size = 96; const left = 140; const maxWidth = W - left - 28;
  for (; size > 24; size -= 4) { ctx.font = `800 ${size}px "Arial Narrow", Arial, sans-serif`; if (ctx.measureText(text).width <= maxWidth) break; }
  ctx.fillText(text, left, H / 2);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 4; return texture;
}

function makeConveyorTexture(): THREE.CanvasTexture {
  // matte rubber belt (18.1): dark rubber field, cleat ribs, painted teal
  // directional chevrons, and steel side rails — NOT an emissive neon plane
  const canvas = document.createElement("canvas"); canvas.width = 256; canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#26292d"; ctx.fillRect(0, 0, 256, 256);
  ctx.fillStyle = "#1c1f22";
  for (let y = 12; y < 256; y += 26) ctx.fillRect(8, y, 240, 6);
  ctx.strokeStyle = "#4f9f96"; ctx.lineWidth = 8; ctx.lineCap = "square";
  for (const y of [74, 154, 234]) {
    ctx.beginPath(); ctx.moveTo(82, y - 22); ctx.lineTo(128, y + 14); ctx.lineTo(174, y - 22); ctx.stroke();
  }
  ctx.fillStyle = "#7f878f"; ctx.fillRect(0, 0, 8, 256); ctx.fillRect(248, 0, 8, 256);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.repeat.set(1, 1.8); return texture;
}
